import { apiErrorMessage, customFetch, retryTransient } from '@agos/api-client'
import type { PatchOrdersOrderIdBody } from '@agos/api-client/model'
import {
  postCouponsValidate,
  useGetBurgerBoxConfig,
  useGetCategories,
  useGetPizzaIngredients,
  useGetProducts,
  useGetSurcharges,
  useGetVatCategories,
} from '@agos/api-client/menu'
import { useGetSettingsLunch, useGetSettingsPizzaConfig } from '@agos/api-client/platform'
import {
  getGetOrdersOrderIdQueryKey,
  getGetOrdersQueryKey,
  useGetDeliveryZones,
  useGetDeliveryZonesDepot,
  useGetOrdersOrderId,
  useGetSalesChannels,
  usePatchOrdersOrderId,
  usePostOrders,
} from '@agos/api-client/orders'
import { getCustomersLookup } from '@agos/api-client/customers'
import { useGetEmployeesWaiters } from '@agos/api-client/hr'
import {
  T,
  afterOrderRoute,
  boardChannelOf,
  categoryChipStyle,
  categoryColour,
  filterChannelsOf,
  formatCents,
  formatDayLabel,
  gramazFor,
  asksUpsellFor,
  normalisePizzaConfig,
  offerUpsell,
  offersAddonsOn,
  offersSizeChoice,
  orderableChannels,
  parseMoneyToCents,
  pizzaSections,
  productSizes,
  requirementsFor,
  settlesAtOnce,
  shiftDate,
  sizeWeightText,
  toDateParam,
  tt,
  type CategoryColour,
  type FilterChannel,
  type ProductSize,
  type SizedProduct,
  type UpsellPrompt,
  useAuth,
  useModalFocus,
} from '@agos/shared'
import {
  AddonPrompt,
  BUTTON_PRIMARY,
  CategoryIcon,
  ConfirmDialog,
  EmptyState,
  PageHeader,
  ReadFailedBlock,
  cn,
  lunchSectionIconName,
  toast,
} from '@agos/ui'
import {
  groupKey, productSections } from './product-groups'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Boxes, CheckCircle2, Gift, Loader2, Pizza, Search, ShoppingBag, ShoppingCart, Store, Truck, X } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'
import { useLocation, useSearch } from 'wouter'

import { CustomerPanel } from './CustomerPanel'
import {
  autofilledLabel,
  filledFrom,
  matchOf,
  worthLookingUp,
} from './customer-lookup'
import {
  earliestTime,
  noTimeLeftToday,
  lunchMismatch,
  partsOf,
  scheduledAtOf,
  scheduledLabelOf,
  todayInZone,
} from './preorder'

import {
  CartDiscountSection,
  CartItemsList,
  CartNotes,
  CartPaymentMethods,
  CartPreorder,
  CartTotalsPanel,
  type CouponState,
} from './OrderCart'
import {
  addAddonLines,
  addBoxLines,
  addProduct,
  basketCount,
  basketCountLabel,
  changeQuantity,
  chosenAddonsOf,
  extrasBasePrice,
  parentLineIdFor,
  quantityOf,
  removeBoxGroup,
  removeLine,
  setLineExtras,
  withLineId,
  type OrderCartLine,
} from './cart'
import { lineNoteOf, setLineNote } from './line-note'
import {
  unprintableProductNames,
  unprintableSurchargeIds,
  unprintableWarning,
} from './unprintable-lines'
import {
  boxById,
  boxesForCategory,
  boxGroupId,
  boxLines,
  boxProblems,
  boxProductOf,
  boxSummary,
  choicesForSlot,
  fixedForSlot,
  type Selections,
} from './burger-box'
import {
  EMPTY_PIZZA,
  newPizza,
  pizzaDraftOnLoad,
  basePriceCents,
  basesFor,
  ingredientGroups,
  portionLabel,
  portionPriceCents,
  PIZZA_NAME_MAX,
  ingredientQty,
  isKomboEnabled,
  komboLineName,
  komboSizeCm,
  komboPizzaCount,
  komboCountLabel,
  lunchMealDescription,
  pizzaCartId,
  pizzaLineName,
  pizzaParts,
  pizzaPriceCents,
  pizzaProblems,
  pizzaSummary,
  sizesOf,
  toggleIngredient,
  type BuilderIngredient,
  type KomboPizza,
  type PizzaDraft,
} from '@agos/shared'
import {
  editPayload,
  editScalars,
  editSummary,
  editLoadProblem,
  existingIdsOf,
  loadedOrderOf,
  lockedNote,
  mayChangePaymentMethod,
  paymentMethodLockedNote,
  editRefusal,
  mayEdit,
  needsAFiskalDecision,
  printedWarning,
  reopenedDraft,
  seededDiscountOf,
  unseededServerLines,
  untouchedDiscountCents,
  withLoadedExtras,
  type EditScalars,
} from './order-edit'
import { APPLY_GIFT, discountBoxesFor, EDIT_ORDER, editorRefusal } from '../order-rights'
import { FiskalDecisionDialog } from './FiskalDecisionDialog'
import { cartDepositCents, cartSubtotalCents, cartTotals, type DiscountMode } from './cart-math'
import {
  belowMinimum,
  chosenZone,
  deliveryProblems,
  feeCentsFor,
  feeNote,
  zoneChoicesOf,
  zoneForAddress,
} from './delivery-fee'
import {
  emptyDayNote,
  lunchCartLine,
  lunchIsOffered,
  lunchPayloadOf,
  repriceLunchSoups,
  soupBundlePriceOf,
  mealPriceNote,
  sectionsFor,
} from './lunch-tab'
import { giftLinesFor } from './gift-rules'
import { useCartGifts } from './cart-gifts'
import type { OrderListItem } from '@agos/api-client/model'
import { buildOrderPayload, type OrderPayload } from './order-payload'
import { zoneForPoint } from './zone-from-address'
import {
  blockMatchesOf,
  blockMatchNote,
  blockMatchPath,
  courierQuoteOf,
  DELIVERY_QUOTE_PATH,
  formatDecimal,
  quotePath,
  usablePoint,
  zoneOutlinesOf,
  type LatLng,
} from '@agos/shared'

import { blockLookupOf } from './blocked-warning'
import { tillCourierFeeCents, tillCourierNote } from './courier-fee'
import { loadedPointOf, pointForTypedAddress } from './delivery-point'
import { zoneAfterPin } from './pin-drop'
import {
  initialWaiterId,
  waiterListState,
  waiterOptions,
  waiterPlaceholder,
  waiterProblem,
} from './waiter-choice'
import { isPizzaCategory, isPizzaProduct } from './pizza-extras'
import { PizzaExtrasDialog } from './PizzaExtrasDialog'
import { KomboPizzaDialog } from './KomboPizzaDialog'
// §9.212 — the restaurant dialog, unchanged: it already holds the fiškál choice and the cash/card
// choice, which is what *ako v reštauračnom toku* asks for.
import { PaymentDialog } from '../restaurant/PaymentDialog'
import { useKitchenCalls } from '../kitchen-calls/use-kitchen-calls'

/**
 * `/new-order` — the order builder, ported from legacy `pages/new-order.tsx`.
 *
 * The cart's arithmetic, its mutations, the gift entitlement and the payload
 * shape are all pure functions with tests beside them (`cart-math.ts`,
 * `cart.ts`, `gift-rules.ts`, `order-payload.ts`). What is left here is the
 * screen: pick a channel, pick products, fill in who it is for, send it.
 */

/**
 * The icon each channel gets, by what it *does* rather than what it is called.
 *
 * §9.5 replaced the hard-coded three with the franchise's own `sales_channels`, so a fourth channel needs a
 * sensible glyph without being named here: one that takes an address is a van, one that serves tables is a
 * restaurant, everything else is a counter.
 */
function iconFor(channel: FilterChannel) {
  if (channel.requiresDeliveryAddress) return Truck
  if (channel.usesTables) return Store
  return ShoppingBag
}

const FIELD_CLASSES =
  'w-full px-3 py-2 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20'

/** How long the confirmation stays up before the screen moves on. */
const CONFIRMATION_MS = 2000

interface ProductLike {
  id?: number
  name?: string
  price?: number | string
  categoryId?: number
  categoryName?: string
  isActive?: boolean
  /**
   * §9.82 — what the tile draws besides the name. All four were already on the wire and none of them
   * were named here, which is the whole of the defect: the manager had them in hand and dropped them.
   */
  description?: string | null
  unitAmount?: string | number | null
  unit?: string | null
  sortOrder?: number
  /** §9.86 — the five named groups legacy splits the pizza category into. */
  pizzaSubcategory?: string | null
  /**
   * §9.730 - `pizza` for a pizza wherever it is filed. Read beside the category by `isPizzaProduct`, so a pizza in
   * *Jesenné menu* (Nesvady's *Kačacia pizza*) takes toppings at the till as it does on a saved order.
   */
  productType?: string | null
}

interface CategoryLike {
  id?: number
  name?: string
  /** §9.91 — the icon the franchise gave this category, drawn over its daily-menu section. */
  icon?: string | null
  /** §9.15. Absent on a franchise that has never coloured its menu, which is most of them today. */
  color?: string | null
  /** §9.82 — the order the owner arranged in the menu editor. */
  sortOrder?: number
}

export function NewOrderPage() {
  const queryClient = useQueryClient()
  const { user, hasPermission } = useAuth()
  const [, navigate] = useLocation()
  const search = useSearch()
  // §9.720, owner O95 - the kitchen's zvonček rings while a waiter is taking an order.
  useKitchenCalls()

  const initialType = new URLSearchParams(search).get('type') ?? 'delivery'
  const editId = Number(new URLSearchParams(search).get('edit') ?? '') || null
  /* §9.744, owner O114 - an edit asks `edit_order`, the manual discount `order_discount` and the coupon `apply_coupon`
     (BACKEND.md §10A.397). Read once, so the editor, the two boxes and the save below answer the same question. */
  const mayEditOrder = hasPermission(EDIT_ORDER)
  const discountBoxes = discountBoxesFor(hasPermission)
  // §9.762, owner O124 - the gift offers, and the reads behind them, on `apply_gift` (BACKEND.md §10A.416).
  const mayApplyGift = hasPermission(APPLY_GIFT)
  const initialTable = new URLSearchParams(search).get('table') ?? ''
  // `/preorders` has had a **Nová časenka** button pointing here since it was ported, and nothing read
  // the flag — it opened an ordinary order builder and the pre-order was lost. FB-193.
  const initialCasenka = new URLSearchParams(search).get('casenka') === '1'

  const [orderType, setOrderType] = useState(initialType)

  // §9.5: the franchise's own channels, and only the switched-on ones — a new order cannot go on a channel
  // somebody turned off.
  const { data: salesChannels } = useGetSalesChannels()
  const channels = useMemo(() => orderableChannels(filterChannelsOf(salesChannels)), [salesChannels])
  /**
   * Where this screen goes after a save - §9.444's role rule, and §9.725: a waiter whose order is on its channel's own
   * *Otvorené stoly* lands there, because the ordinary one leaves that channel's orders out (§9.688). One function for
   * the four places a save navigates, so none of them can be the one that lands on the wrong board.
   */
  const savedOrderRoute = (options: { preorder?: boolean } = {}) =>
    afterOrderRoute(user?.systemRole, {
      ...options,
      ownBoard: boardChannelOf(orderType, filterChannelsOf(salesChannels))?.slug ?? null,
    })
  // What this channel needs, read off its capabilities. `SalesChannel`: ask what it does, never what it is
  // called — the checks below were `orderType === 'restaurant'` and `=== 'delivery'`, so a fourth channel
  // would have been asked for nothing at all.
  const needs = requirementsFor(orderType, channels)
  /**
   * §9.212 — the owner's *Do ruky*: taken, paid and printed without leaving the screen.
   *
   * Asked of the channel's capability, never of its slug (§10A.87), so a franchise calling it
   * *Výdaj z okna* gets the same flow.
   */
  const settlesHere = settlesAtOnce(orderType, channels)
  /**
   * §9.657, owner O44 - whether this channel asks *Chcete si priobjednať niečo navyše?* after an add.
   *
   * The owner: *"we have in customer website that «Doplnky k produktu» ... can you make also setting in Predajné
   * Kanály - to show these modals for čašníčka?"*. `offersAddonsOn` also refuses a channel that settles at once,
   * because such an order prints its document on save and there is no moment left to ask.
   */
  const asksAboutAddons = offersAddonsOn(orderType, channels)
  // §9.712 - the question, for the line it was asked about and the units added to it (`upsellPromptAfter`).
  const [addonPrompt, setAddonPrompt] = useState<UpsellPrompt | null>(null)
  /**
   * §9.712, owner answer #136 - **every added unit is asked about**: the tile, a size chip and the basket's *+*. Before
   * it only the tile of an unsized product asked, so the second burger never offered its fries.
   *
   * Gated on §9.657's per-channel switch, read off the channel the order is on. **Add first, ask second**: every
   * caller has already put the unit in the basket, and `offerUpsell` never undoes it - a failed lookup is swallowed and
   * a product with no upsells opens nothing.
   *
   * §9.713 - the key is the `lineId` of the košík line the unit landed on (`parentLineIdFor`, `withLineId`), which is
   * what makes two quick presses on one line one question for two units rather than the second replacing the first,
   * and what the confirm reads to put the chosen add-ons under that line (`addAddonLines`).
   */
  const offerUpsellFor = (product: { id: number; name: string }, parentLineId: string | null) => {
    if (!asksAboutAddons || product.id <= 0 || parentLineId === null) return
    void offerUpsell({ productId: product.id, productName: product.name, parentKey: parentLineId }, setAddonPrompt)
  }
  const [tableName, setTableName] = useState(initialTable)
  const [cart, setCart] = useState<OrderCartLine[]>([])
  /**
   * Every `existingId` this cart has been seeded with - §9.433, FB-756.
   *
   * What makes *the cart never had this line* distinguishable from *the operator deleted this line*.
   * Without it the two are the same state and the save sends a deletion for both.
   */
  const [seededIds, setSeededIds] = useState<ReadonlySet<number>>(new Set())
  const [activeCategory, setActiveCategory] = useState<number | null>(null)
  const [productSearch, setProductSearch] = useState('')
  const [customerName, setCustomerName] = useState('')
  const [customerPhone, setCustomerPhone] = useState('')
  const [customerAddress, setCustomerAddress] = useState('')
  // §9.693, FB-1003 - where the delivery goes, beside the text that names it. `delivery-point.ts` holds the rules.
  const [deliveryPoint, setDeliveryPoint] = useState<LatLng | null>(null)
  const [customerEmail, setCustomerEmail] = useState('')
  // §9.14. `looking` drives *Hľadám zákazníka…*, `autofilledFrom` drives *Doplnené z databázy*.
  const [looking, setLooking] = useState(false)
  const [autofilledFrom, setAutofilledFrom] = useState<string | null>(null)
  const [pastAddresses, setPastAddresses] = useState<string[]>([])
  const lookupTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [zoneId, setZoneId] = useState<string | null>(null)
  // §9.161 — only reachable when the address matches no zone; legacy's *"ručne nižšie"*.
  const [manualDeliveryFee, setManualDeliveryFee] = useState('')
  // §9.786, owner O139 - where external couriers deliver, the fee box over the courier quote. Empty means *the quote*.
  const [courierFee, setCourierFee] = useState('')
  // §9.40. Asked only where a channel seats people — the same flag the backend's `requiresWaiter()`
  // reads, so the two ends agree by construction rather than by coincidence.
  /**
   * §9.42 — the two functions §9.7's note said were "the only two ways", and were not.
   *
   * They were six inline handlers, and the three that open a panel left `activeCategory` where it
   * was — so leaving *Nápoje* for the daily menu lit both chips. Each of these sets **both** halves,
   * so neither can be called and leave the other stale.
   */
  const chooseCategory = (id: number | null) => {
    setPanel('none')
    setActiveCategory(id)
  }

  const [komboOpen, setKomboOpen] = useState(false)
  const [extrasLine, setExtrasLine] = useState<number | null>(null)
  const [waiterId, setWaiterId] = useState<number | null>(null)
  const [waiterTouched, setWaiterTouched] = useState(false)
  /* §9.633, FB-948 - `isError` as well as `isSuccess`. A failed read leaves `waitersLoaded` false **and**
     the list empty, so the refusal said *still loading* for ever while the dropdown said *nobody on shift*.
     `isSuccess` is kept because §9.320's preselection is right to treat *not yet* and *failed* alike - what
     needed splitting is the sentence. */
  const {
    data: waiterData,
    isSuccess: waitersLoaded,
    isError: waitersFailed,
    refetch: refetchWaiters,
  } = useGetEmployeesWaiters({
    query: { enabled: needs.table },
  })
  const waiters = useMemo(() => waiterOptions(waiterData), [waiterData])
  /* §9.633 - one state, two readers: the refusal and the dropdown's placeholder. They disagreed because each
     derived its own answer, one from the flag and one from the array. */
  const waiterList = waiterListState(waitersLoaded, waitersFailed, waiters)
  // Yourself, when you are a waiter and on the shift list. Once: re-running it would undo a manager
  // who had just chosen somebody else.
  if (!waiterTouched && waiters.length > 0) {
    setWaiterTouched(true)
    const mine = initialWaiterId(user, waiters)
    if (mine !== null) setWaiterId(mine)
  }
  /**
   * Which panel the builder is showing, as **one** value.
   *
   * It was three booleans that had to be mutually exclusive, and keeping them so was left to each
   * click handler. Two of them did not: an ordinary category chip set `activeCategory` and cleared
   * none of the panels, and the render checks the panels first — so after opening the daily menu, the
   * burger box or a custom pizza, **every other category stopped responding**. The chip highlighted,
   * the panel stayed, and a waitress mid-order was stuck with no way back but *Všetko*. The lunch chip
   * also left `pizzaOpen` set, so pizza → lunch showed pizza.
   *
   * One value cannot disagree with itself. `chooseCategory` and `openPanel` below are the only two ways
   * to move, and each sets the whole selection. FB-191.
   */
  const [panel, setPanel] = useState<'none' | 'lunch' | 'box' | 'pizza'>('none')
  const lunchOpen = panel === 'lunch'
  const boxOpen = panel === 'box'
  const pizzaOpen = panel === 'pizza'
  const [boxSelections, setBoxSelections] = useState<Selections>({})
  const [pizza, setPizza] = useState<PizzaDraft>(EMPTY_PIZZA)
  /** §9.724 - the note of the line `reopenPizza` took this draft out of, until the builder adds it back. */
  const [reopenedNote, setReopenedNote] = useState<string | undefined>(undefined)
  // The lunch menu is per day, and the page opens on today's — `mealsForDate` falls back to the first
  // authored week, which is legacy behaviour for a franchise that wrote one week and never dated it.
  const [lunchDate, setLunchDate] = useState(() => toDateParam(new Date()))
  const [notes, setNotes] = useState('')
  // Legacy starts both empty and the toggle clears them again on the way off, so a switched-off časenka
  // never leaves a half-typed time behind to be sent by the next order.
  const [preorderOn, setPreorderOn] = useState(initialCasenka)
  const [preorderDate, setPreorderDate] = useState('')
  const [preorderTime, setPreorderTime] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('cash')
  const [discountMode, setDiscountMode] = useState<DiscountMode>('percent')
  const [discountValue, setDiscountValue] = useState('')
  /* §9.744 amended, owner O114 - whether the operator touched the *Zľava* box in this edit: typed in it, switched its
     unit or cleared it. Untouched, the save says nothing about the discount and the cart shows the figure the server
     will store (`untouchedDiscountCents`). The seed below resets it on every load. */
  const [discountTouched, setDiscountTouched] = useState(false)
  const [couponCode, setCouponCode] = useState('')
  const [coupon, setCoupon] = useState<CouponState | null>(null)
  const [submitted, setSubmitted] = useState(false)
  /**
   * §9.312, owner P3 - asked before the basket is emptied.
   *
   * **Not an undoable click.** Somebody has spent minutes building this, often with a customer on the
   * phone, and there is no undo on the till. Every other destructive action on the manager goes
   * through `ConfirmDialog` and so does this.
   */
  const [confirmClear, setConfirmClear] = useState(false)
  /**
   * The order just created, waiting to be paid — §9.212.
   *
   * Only ever set for a channel that settles at once. Everything else keeps landing on the board
   * after its confirmation, which is where an unsettled order is looked after.
   */
  const [settling, setSettling] = useState<OrderListItem | null>(null)
  const [loadedId, setLoadedId] = useState<number | null>(null)

  // §9.378, FB-673 - **the till follows the menu.** `refetchOnWindowFocus` is off for the whole app
  // (deliberately: figures must not move under somebody reading them) and this screen stays open for
  // a whole shift, so a size renamed or added in *Pizza / nastavenia* never reached the grid - it
  // went on offering the label it had loaded hours earlier, and `ServerPricing` refused the order
  // with *"Táto veľkosť už nie je v ponuke"*. Five minutes is the same order of magnitude as the
  // dispatch board's poll, and a products list is small.
  /* §9.633 - the grid's empty state is *Žiadne produkty / Skúste zmeniť filtre*, an instruction, and the
     owner's menu holds 177 active orderable products. The five-minute refetch cuts both ways: a transient
     failure recovers, and until it does the grid asserts the menu is empty. */
  const {
    data: products,
    isError: productsFailed,
    refetch: refetchProducts,
  } = useGetProducts({}, { query: { refetchInterval: 5 * 60_000 } })
  // §9.111 — the same three sources the settings screens read, so this cannot disagree with what
  // `/surcharges` says on its own page.
  const { data: surchargeData } = useGetSurcharges()
  const { data: vatCategoryData } = useGetVatCategories()
  const unprintableNote = useMemo(() => {
    const blocked = unprintableSurchargeIds(
      Array.isArray(surchargeData) ? surchargeData : [],
      Array.isArray(vatCategoryData) ? vatCategoryData : [],
    )
    return unprintableWarning(
      unprintableProductNames(cart, Array.isArray(products) ? products : [], blocked),
    )
  }, [cart, products, surchargeData, vatCategoryData])
  // Only asked where an order can be delivered — a collection has no zone and no fee.
  const { data: zoneData } = useGetDeliveryZones({ query: { enabled: needs.address } })
  const { data: lunchData } = useGetSettingsLunch()
  const { data: boxData } = useGetBurgerBoxConfig()
  const { data: pizzaConfigData } = useGetSettingsPizzaConfig()

  /* §9.432 - `isError` is read, not discarded. The save used to branch on `editing !== null` alone,
   * so an order that had not arrived - or had failed - fell through to `createOrder` and made a
   * second order for the same table, holding only what the operator had just typed. */
  const {
    data: editData,
    isError: editFailed,
  } = useGetOrdersOrderId(editId ?? 0, { query: { enabled: editId !== null } })
  const editing = useMemo(() => loadedOrderOf(editData), [editData])
  const updateOrder = usePatchOrdersOrderId()
  /* §9.620 - the patch the server refused for want of a decision about a live fiscal document, held so
   * either route of `FiskalDecisionDialog` can re-send it. Null is *no question open*; `const` is not
   * hoisted, so this has to be declared above `sendEdit` (§9.487). */
  const [fiskalQuestion, setFiskalQuestion] = useState<PatchOrdersOrderIdBody | null>(null)
  const { data: ingredientData } = useGetPizzaIngredients()
  const ingredients = useMemo(
    () => (Array.isArray(ingredientData) ? (ingredientData as BuilderIngredient[]) : []),
    [ingredientData],
  )

  const { data: categories } = useGetCategories()
  const createOrder = usePostOrders()

  const productList = useMemo(
    () => (Array.isArray(products) ? (products as ProductLike[]) : []).filter(
      (product) => product.isActive !== false,
    ),
    [products],
  )
  const categoryList = useMemo(
    () =>
      (Array.isArray(categories) ? (categories as CategoryLike[]) : []).filter(
        // §9.189 / §10A.81 — a category can be kept out of this picker without touching the customer
        // site. `!== false` is §9.155's rule: a payload from before the column reads as visible, so
        // no franchise loses a category by upgrading. Its products still price and store — a kombo
        // built from a hidden category is the case the switch exists for.
        (category) => (category as { visibleInManager?: boolean }).visibleInManager !== false,
      ),
    [categories],
  )

  const lunch = useMemo(() => lunchPayloadOf(lunchData), [lunchData])
  const lunchSections = useMemo(() => sectionsFor(lunch, lunchDate), [lunch, lunchDate])

  // §9.118 — every meal the loaded payload knows, not just the day on screen: the cart keeps its
  // lines when the tab moves to another date, and a line the pass cannot recognise is a line it
  // silently leaves at the wrong price.
  const lunchMeals = useMemo(
    () => lunch.weeks.flatMap((week) => week.days.flatMap((day) => day.meals)),
    [lunch],
  )
  const lunchBundlePrice = useMemo(() => soupBundlePriceOf(lunch), [lunch])

  /**
   * §9.118 — a soup costs less while there is a main to pair it with, and costs full price again the
   * moment that main is removed.
   *
   * Every cart mutation goes through here, which is the point: legacy needs a `promoteOneSoup` on
   * adding a main *and* a `demoteOneSoup` on removing one, and the removal half is the one that gets
   * forgotten — a cancelled main would leave the soup discounted for ever. A pass over the whole cart
   * has no halves. Wrapping the setter rather than watching the result in an effect also keeps this
   * out of the render cycle, so there is no cascading update to guard against.
   */
  const updateCart: typeof setCart = (update) => {
    setCart((current) => {
      const next = typeof update === 'function' ? update(current) : update
      return repriceLunchSoups(next, lunchMeals, lunchBundlePrice)
    })
  }

  const pizzaConfig = useMemo(
    () => normalisePizzaConfig(pizzaConfigData as Parameters<typeof normalisePizzaConfig>[0]),
    [pizzaConfigData],
  )
  /**
   * Close a panel and put the category back - §9.331.
   *
   * **The owner:** *"when I was in nová objednávka -> pizza -> vlastná pizza, and closed it, it was
   * bugged after and it was removed, Ive seen a lot of things Ii did not have access to"*.
   *
   * `openPanel` clears `activeCategory`, and closing put nothing back - so the grid returned as
   * *Všetko*. That is the whole report: the *Vlastná pizza* and *Kombo* tiles only render inside the
   * pizza category, so they were **gone** (*"it was removed"*), and the grid was showing every
   * product in the menu instead of the eight pizzas (*"a lot of things Ii did not have access to"*).
   *
   * The clearing itself stays. It is an owner decision recorded by a spec - *"tá kategória kde som
   * bol predtým, zostane označená"* was them reporting two lit chips - so the chips keep behaving
   * that way and only the return trip is new.
   */
  const [panelCategory, setPanelCategory] = useState<number | null>(null)
  const closePanel = () => {
    setPanel('none')
    setActiveCategory(panelCategory)
  }
  /* §9.488, `TODO.md` 3c - the trap and the Escape. Declared after `closePanel` because it calls it, and
     `closePanel` is what the dismiss scrim already uses - one close for the pointer and the keyboard. */
  const pizzaPanelRef = useRef<HTMLElement>(null)
  const { onKeyDown: pizzaKeyDown } = useModalFocus(true, pizzaPanelRef, closePanel)
  const openPanel = (kind: 'lunch' | 'box' | 'pizza') => {
    setPanel(kind)
    // Only when coming from the grid: opening the box panel from inside the pizza panel must not
    // record `null` over the category the pizza panel was opened from.
    if (panel === 'none') setPanelCategory(activeCategory)
    setActiveCategory(null)
    // §9.60 — the franchise's default base, applied on open rather than at mount: `pizzaConfig` is
    // fetched, so a lazy initial state would capture `null` and preselect nothing for good. Applied
    // only to an untouched draft, so reopening the panel cannot wipe a half-built pizza.
    if (kind === 'pizza') {
      setPizza((current) => (current === EMPTY_PIZZA ? newPizza(pizzaConfig, ingredients) : current))
    }
  }

  // §9.717, FB-1019 - a panel opened before the pizza config and the ingredients answered got no base and no cheese
  // for good, because §9.60 applies them on open only. An untouched draft takes them the moment the data arrives.
  // Corrected during render, as the checkout's lunch day is (§9.219): `pizzaDraftOnLoad` answers the same object once
  // there is nothing to apply, so this settles in one pass, and an effect would cost a frame of the empty draft.
  if (panel === 'pizza') {
    const settled = pizzaDraftOnLoad(pizza, pizzaConfig, ingredients)
    if (settled !== pizza) setPizza(settled)
  }

  /**
   * A stored custom pizza goes back into the builder - §9.465, `OWNER-QUESTIONS.md` #36.
   *
   * **The owner:** *"when we have ccustom pizza ordered online, and they want to edit pizza, we cant
   * edit it, its like normal položka and not editable pizza"*. §9.440 made the line say what it is;
   * this opens it.
   *
   * **The line is removed and the pizza is rebuilt**, rather than the line being edited in place. A
   * reopened pizza is re-priced from today's config - which is what #36 answers, *"Re-price from
   * today's menu"* - and the price is a property of the whole draft, not of a piece, so editing the
   * parts of a line that keeps its old price would leave the two disagreeing. Adding it back from the
   * builder goes through the same path a new pizza does, including `boundedByParts` on the server.
   *
   * **It refuses rather than opening wrong.** `draftFromParts` matches by name and says which
   * ingredient or size no longer exists; a pizza that reopens missing a topping would be sent, and
   * nothing on the screen would say a topping went missing. The control is only drawn on a line
   * `reopenablePizza` accepts, so this refusal is the rare case rather than the ordinary one.
   */
  const reopenPizza = (lineIndex: number) => {
    const line = cart[lineIndex]
    if (line === undefined) return

    /* §9.703, FB-1009 - a pizza built in this košík comes back as its own draft, 3× and all; one off a saved
     * order is rebuilt from its parts, with the line's own name as `draftFromParts`' fourth argument (§9.680). */
    const rebuilt = reopenedDraft(line, pizzaConfig, ingredients)
    if (rebuilt.draft === null) {
      toast({ variant: 'destructive', title: tt('Pizzu sa nedá otvoriť'), description: rebuilt.refusal })
      return
    }

    /* §9.703 - **one pizza comes out of the line, not the whole line.** Two identical custom pizzas are one line
     * of quantity two, and removing it threw the second pizza away while the first was being edited. */
    updateCart((current) => changeQuantity(current, lineIndex, -1))
    setPizza(rebuilt.draft)
    /* §9.724, FB-1024 - the line's note goes with its draft, or the pizza comes back without what the kitchen was told
     * and a loaded order's save deletes the stored note. */
    setReopenedNote(lineNoteOf(line))
    openPanel('pizza')
  }

  // §9.44 — the kombo's candidates are the franchise's own pizzas, by the category name the customer
  // app matches on too. `selectablePizzas` drops the withdrawn ones.
  const komboPizzas = useMemo(
    () =>
      productList
        .filter((product) => isPizzaCategory(product.categoryName))
        .map((product) => ({
          id: product.id ?? 0,
          name: product.name ?? '',
          price: product.price,
          pizzaSizes: (product as { pizzaSizes?: unknown }).pizzaSizes,
          active: product.isActive,
        })) as KomboPizza[],
    [productList],
  )
  const komboCm = komboSizeCm(pizzaConfig)
  // §9.798 - how many pizzas the box holds, the franchise's setting; 3 exactly when it was never set.
  const komboCount = komboPizzaCount(pizzaConfig)

  // §9.45 — the first size the franchise sells, chosen for them. Set during render like the waiter
  // picker above, not in an effect, and only while nothing has been chosen.
  const firstPizzaSize = sizesOf(pizzaConfig)[0]?.cm ?? null
  if (pizza.sizeCm === null && firstPizzaSize !== null) {
    setPizza((current) => (current.sizeCm === null ? { ...current, sizeCm: firstPizzaSize } : current))
  }


  // §9.46 — the box for the category being looked at, as the customer app has always done.
  const boxes = useMemo(() => boxesForCategory(boxData, activeCategory), [boxData, activeCategory])
  const [chosenBoxId, setChosenBoxId] = useState<string | null>(null)
  /**
   * Which box the panel is filling — §9.209.
   *
   * Derived rather than reset in an effect: an id belonging to the previous category simply does not
   * match, and the fallback is this category's first. An effect would leave one render showing the
   * wrong panel, which on a till is a line added to somebody's order.
   */
  /**
   * **By id from the whole payload first - §9.331.** The owner: *"when I opened stundentský burger
   * box, it opened normal burger box"*. `openPanel` clears `activeCategory` on the same click that
   * set `chosenBoxId`, so `boxes` was recomputed for *no category* - which `boxesForCategory` answers
   * with the first active box in the payload - and the id matched nothing, and the fallback below
   * opened that first box.
   *
   * The fallback stays for what §9.209 wrote it for: nothing chosen, or an id from the category just
   * left. It is simply not the right way to answer *which box was clicked*.
   */
  const box = boxById(boxData, chosenBoxId) ?? boxes.find((entry) => entry.id === chosenBoxId) ?? boxes[0] ?? null
  const boxProducts = useMemo(() => productList.map((product) => boxProductOf(product)), [productList])

  // The order is poured into the form once, when it arrives. Re-running it on every render would
  // undo whatever the person editing had just typed.
  if (editing !== null && loadedId !== editing.id) {
    setLoadedId(editing.id)
    setSeededIds(new Set(existingIdsOf(editing.lines)))
    updateCart(editing.lines)
    setOrderType(editing.orderType)
    setCustomerName(editing.customerName)
    setCustomerPhone(editing.customerPhone)
    // §9.383, FB-681 - loaded so it can be diffed. Left unloaded, an edit that sends `customerEmail`
    // sends an empty one and clears the customer's address.
    setCustomerEmail(editing.customerEmail)
    setCustomerAddress(editing.customerAddress)
    setDeliveryPoint(loadedPointOf(editing))
    setTableName(editing.tableName)
    setNotes(editing.notes)
    /* §9.420, FB-747 - the owner's own parenthesis: *"check also when editiing already applied zlava
     * objednávky, if itis there for editing"*. It was not. Nothing here set the discount, because
     * `LoadedOrder` never read one, so an order with a zľava opened with the box empty - and on the
     * first save the diff would have read that empty box as *the discount was removed*.
     *
     * Seeded from `manualDiscountType`/`Value`, which is how the till recorded it, rather than from
     * the total `discount`: a percentage typed as 10 must come back as 10 % and not as the euros it
     * happened to work out to on the basket it was given. */
    /* §9.420 - the fee this order was charged, which is the figure the owner said was missing.
     *
     * `manualDeliveryFee` started `''` and nothing ever seeded it, so the edit screen showed either
     * the zone's **current** fee or nothing at all - never the order's own. That is FB-747's
     * *"dovoz was not shown in edit"* exactly. */
    setManualDeliveryFee(editing.deliveryCents === 0 ? '' : String(editing.deliveryCents / 100))
    /* §9.431, the owner's second report - *"when you add zľava, and edit it, zľava is not saved and
     * you need to type it again"*.
     *
     * **This used to be gated on `manualDiscountType !== ''` alone, and 1 620 of the owner's orders
     * have a discount with no type recorded** - a coupon records none, the column is for a *manual*
     * discount, and neither do the rows that predate it. For all of them the box opened empty, and
     * §9.420's own comment says what happens next: the diff reads that empty box as *the discount
     * was removed*. It does, on any save, for any reason.
     *
     * Seeding it is the whole fix, and the reason is worth stating: with the box holding the order's
     * real figure, `current.discountCents` equals `loaded.discountCents`, so the diff sends
     * **nothing** and the discount is preserved as it was rather than rewritten. It only becomes a
     * manual discount if the operator edits the box - which is the moment they *are* the person who
     * gave it. */
    /* §9.744 - one rule now, `seededDiscountOf`, and **seeded whether or not the box is drawn**: without
     * `order_discount` the box is hidden, and the figure it would have shown stays in the form, so the diff
     * sends nothing about a discount this person was not allowed to touch. */
    const seededDiscount = seededDiscountOf(editing)
    if (seededDiscount !== null) {
      setDiscountMode(seededDiscount.mode)
      setDiscountValue(seededDiscount.value)
    }
    // §9.744 amended - a freshly loaded order's box is untouched, whatever was typed before it arrived.
    setDiscountTouched(false)
    setPaymentMethod(editing.paymentMethod)
    // Legacy `new-order.tsx:571`. Without this, opening a časenka to add a drink and saving would send
    // the order back with its time blank — the kitchen would start cooking it now.
    setPreorderOn(editing.isPreorder)
    const scheduled = editing.scheduledAt === '' ? { date: '', time: '' } : partsOf(editing.scheduledAt)
    setPreorderDate(scheduled.date)
    setPreorderTime(scheduled.time)
  }

  /* §9.433, FB-756 - a line the server has that this cart was never given.
   *
   * The pour above happens once per order id, which is right: re-running it would undo whatever the
   * person editing had just typed. It also means a line the order gains *after* that pour never
   * reaches the cart - and `editPayload` reads a loaded id the cart does not hold as quantity nought,
   * *the operator removed this line*, which it can only send as a whole-list replace. That is how a
   * drink added by one edit was deleted by the next.
   *
   * Appending rather than re-seeding, so nothing typed is lost; and keyed on what the cart has *ever*
   * been seeded with, so a line the operator really did remove stays removed. The stale detail cache
   * was the way in here and it is fixed at the source too, but a poll, a refetch on focus or a second
   * till editing the same table all arrive the same way. */
  /** Either mutation in flight - §9.433. An edit goes through `updateOrder`, not `createOrder`. */
  const saving = createOrder.isPending || updateOrder.isPending

  const unseeded = unseededServerLines(editing, seededIds)
  if (editing !== null && loadedId === editing.id && unseeded.length > 0) {
    setSeededIds((current) => {
      const next = new Set(current)
      for (const id of existingIdsOf(unseeded)) next.add(id)
      return next
    })
    updateCart((current) => [...current, ...unseeded])
  }

  /* §9.730, owner O100 - a saved pizza's toppings read back out of its bracket once the ingredients answer, so the
   * dialog's change takes off what the line was charged for and the save sends the toppings it was sold with.
   * Corrected while rendering, like the gifts below: `withLoadedExtras` answers the same array once there is nothing
   * to do. **A functional update**, because the merge above may be queued in the same render and a value computed
   * from this render's `cart` would overwrite it. */
  if (withLoadedExtras(cart, ingredients) !== cart) {
    setCart((current) => withLoadedExtras(current, ingredients))
  }

  const zoneChoices = useMemo(() => zoneChoicesOf(zoneData), [zoneData])

  // §9.346 - the same depot the checkout reads, because a tie of equally-named zones falls through
  // to `pickZone`, whose step 4 is nearest-the-depot - and since §9.801 every zone for a point does. A client computing it from its own geocode
  // would quote a fee the customer is not charged (§10A.121).
  const { data: depotData } = useGetDeliveryZonesDepot({ query: { staleTime: 30 * 60_000 } })
  const depot = useMemo(() => {
    // No cast: `DepotResponse` already declares both as nullable numbers, and the endpoint answers
    // nulls together when the depot address cannot be geocoded - which both sides read as *fall back
    // to the smallest radius*.
    const row = depotData ?? {}
    return typeof row.lat === 'number' && typeof row.lng === 'number'
      ? { lat: row.lat, lng: row.lng }
      : null
  }, [depotData])

  /**
   * The zone an order being edited is already in - §9.421, FB-748.
   *
   * The owner: *"Still received error 'Zóna nerozpoznaná' even tho I am pretty sure since we have
   * address it should be detected."* They were right. `setZoneId` is called from the address box and
   * from the autocomplete, and **neither runs when an order is loaded**, so every edit of a delivery
   * order opened with no zone and printed that sentence under the picker.
   *
   * **Derived, not seeded.** The seeding block above runs once, when `editing` arrives, and
   * `zoneData` may not have landed by then - a seed would resolve to null and never retry. A
   * derivation re-runs when the zones arrive, on its own.
   *
   * **From the coordinates, not the address text**, because that is what the server resolves from - by its own
   * rule since §9.801 (`zoneForPoint`: the served area, then `pickZone` with the order's own address and the
   * depot). Guessing from the words while the numbers sit right there would be a second answer to one question.
   *
   * It answers once the loaded point is gone - typed over - and the zone falls back to `zoneId ?? loadedZoneId`.
   */
  const loadedZoneId = useMemo(() => {
    if (editing === null) return null
    if (editing.deliveryLat === null || editing.deliveryLon === null) return null
    return zoneForPoint({ lat: editing.deliveryLat, lng: editing.deliveryLon }, zoneData, {
      address: editing.customerAddress,
      depot,
    })
  }, [editing, zoneData, depot])

  /**
   * The zone while the order has a point - §9.801, owner O148: *"Make sure nová objednávka picks the zone the same
   * way"*.
   *
   * A point is an address picked from the suggestions, a dropped pin or a loaded order's own, and with one the zone is
   * the server's answer for it: the zones whose served area holds the point, then `pickZone` with **the text in the
   * box**, which is what the order is saved with, and the depot. Derived rather than set by those events, so it follows
   * the box when the pin's reverse lookup writes a place's name into it - the server's step 3 reads that text - and
   * when the zones or the depot arrive late. With no point, the typed text's guess (`zoneId`) is all there is.
   */
  const pointZoneId = useMemo(
    () =>
      deliveryPoint === null
        ? null
        : zoneForPoint(deliveryPoint, zoneData, { address: customerAddress, depot }),
    [deliveryPoint, zoneData, customerAddress, depot],
  )

  const zone = chosenZone(deliveryPoint !== null ? pointZoneId : (zoneId ?? loadedZoneId), zoneChoices)

  /* §9.787, owner O140 - whether the typed phone or e-mail is on the blocklist. Asked once it could match, and the
     answer only warns: the server refuses a blocked contact on a web order, never at the till. */
  const blockLookup = blockLookupOf(customerPhone, customerEmail)
  const blockRead = useQuery({
    queryKey: ['blocked-contact-match', blockLookup?.phone ?? '', blockLookup?.email ?? ''],
    queryFn: () => customFetch<unknown>(blockMatchPath(blockLookup ?? { phone: '', email: '' })),
    enabled: blockLookup !== null,
    staleTime: 60_000,
    retry: retryTransient,
  })
  const blockMatches = blockLookup === null ? [] : blockMatchesOf(blockRead.data)

  /* §9.786, owner O139 - where external couriers deliver, a phone order's fee is the courier's reward: prefilled from
     the server's quote for the address and editable, and the zones are not consulted. The mode is one cached read;
     the quote is asked for a new order with a point only, because an edit keeps the fee it was charged (§9.420). */
  const modeQuote = useQuery({
    queryKey: ['delivery-quote'],
    queryFn: () => customFetch<unknown>(DELIVERY_QUOTE_PATH),
    enabled: needs.address,
    staleTime: 5 * 60_000,
    retry: retryTransient,
  })
  const couriersDeliver = needs.address && courierQuoteOf(modeQuote.data)?.external === true
  /* §9.802, owner O149 - the zones under the pin, to look at only: *"so just they can see their in doručovacia zóna"*.
     None where external couriers deliver, because there no zone prices anything. */
  const zoneOutlines = useMemo(() => zoneOutlinesOf(zoneData), [zoneData])
  const pinMapZones = couriersDeliver ? [] : zoneOutlines
  const pointQuote = useQuery({
    queryKey: ['delivery-quote', deliveryPoint?.lat ?? null, deliveryPoint?.lng ?? null],
    queryFn: () => customFetch<unknown>(quotePath(deliveryPoint)),
    enabled: couriersDeliver && editing === null && deliveryPoint !== null,
    retry: retryTransient,
  })
  const courierQuote = courierQuoteOf(pointQuote.data)
  const courierFeeCents = tillCourierFeeCents(courierFee, courierQuote)

  /**
   * §9.119 — the zone follows the address, as it did in legacy.
   *
   * The owner asked what the picker is for, and the honest answer is that it should rarely be
   * touched: the zones are named after the settlements the caller is already dictating. Auto-selects
   * only on an unambiguous match, and never argues with somebody who has chosen for themselves.
   */
  const zoneChosenByHand = useRef(false)

  /**
   * Fills the zone from a freshly typed address — §9.119.
   *
   * Done where the address changes rather than in an effect: an effect that sets state on every
   * render of a changed value is a cascading render, and the rule that flags it is right. Only ever
   * *fills* — clearing a zone because the address was retyped mid-word would drop a fee the
   * dispatcher can no longer see was there.
   */
  const addressTyped = (next: string) => {
    setCustomerAddress(next)
    // §9.693 - typed text has no point behind it, unless it is typed back to the loaded order's own address.
    setDeliveryPoint(pointForTypedAddress(next, editing))
    if (zoneChosenByHand.current) return
    const guessed = zoneForAddress(next, zoneChoices, depot).zone?.id ?? null
    if (guessed !== null) setZoneId(guessed)
  }

  const visibleProducts = useMemo(() => {
    const needle = productSearch.trim().toLowerCase()
    return productList.filter((product) => {
      // §9.131 — legacy's first filter line, never ported: a product switched off is not for sale,
      // and the till was the one screen still offering it. `!== false` because a row that omits the
      // field is sellable — reading a missing column as *off* would empty the grid.
      if ((product as { active?: boolean }).active === false) return false
      if (activeCategory !== null && product.categoryId !== activeCategory) return false
      if (!needle) return true
      return (product.name ?? '').toLowerCase().includes(needle)
    })
  }, [productList, activeCategory, productSearch])

  // §9.86 — the category the pizza builders belong to, by the same name test the kombo candidates
  // are picked with. `panel === 'none'` because the lunch, box and pizza panels replace this grid.
  const pizzaCategoryOpen =
    panel === 'none' &&
    activeCategory !== null &&
    isPizzaCategory(categoryList.find((entry) => entry.id === activeCategory)?.name)

  // §9.82 — the menu's own order, and a heading per category unless a chip or a search has already
  // said which one. Both of those collapse to the single flat grid the screen has always drawn.
  // §9.85 — one product, one of its sizes, at that size's own price. `ServerPricing` prices the same
  // line from the same column (§10A.33), so the till and the bill cannot disagree.
  function addSized(product: ProductLike, size: ProductSize) {
    const addable = {
      id: product.id ?? 0,
      name: `${product.name ?? ''} – ${size.label}`,
      price: size.priceCents / 100,
      categoryName: product.categoryName,
      size: size.label,
    }
    // §9.713 - the identity of the line this add lands on, as the tile does, so the question below can name it.
    const parentLineId = asksAboutAddons ? parentLineIdFor(cart, addable) : null
    updateCart((current) =>
      addProduct(current, addable, {
        isPizza: isPizzaProduct(product),
        ...(parentLineId === null ? {} : { lineId: parentLineId }),
      }),
    )
    // §9.712, #136 - a size chip is an added unit like the tile, so it is asked about too.
    offerUpsellFor({ id: addable.id, name: addable.name }, parentLineId)
  }

  const productGroups = useMemo<
    Array<{ categoryId: number | null; title: string; products: ProductLike[] }>
  >(() => {
    // §9.86 — inside the pizza category legacy splits the pizzas into its five named subcategories
    // rather than one wall of 29. The customer app has done this since §9.16; the manager never did.
    if (pizzaCategoryOpen && productSearch.trim() === '') {
      return pizzaSections(visibleProducts).map((section) => ({
        categoryId: null,
        // A pizza with no subcategory groups under no heading rather than being dropped.
        title: section.title ?? '',
        products: section.products,
      }))
    }
    return productSections(
      visibleProducts,
      categoryList,
      activeCategory === null && productSearch.trim() === '',
    )
  }, [visibleProducts, categoryList, activeCategory, productSearch, pizzaCategoryOpen])

  /* §9.744 amended, owner O114 - **a touch counts only on an edit, and only on a box that is drawn**: hidden, the box
     is untouched whatever its state says, so a discount this person may not see is never sent. On a new order the
     question does not arise - the box is the order's whole discount. */
  const editsTheDiscount = editing !== null && discountBoxes.discount && discountTouched
  /* §9.744 amended - the discount nobody touched, as the server will store it: a percentage in force follows the
     goods, a fixed one is capped at them, anything else is kept exactly. Undefined on a new order and on a touched box,
     where the cart works it out from what was typed, as before. */
  const untouchedDiscount =
    editing !== null && !editsTheDiscount ? untouchedDiscountCents(editing, cartSubtotalCents(cart)) : undefined

  // The fee depends on the goods total, and the goods total is what `cartTotals` computes — so the
  // subtotal is taken first and fed back in. `deliveryFeeCents` reads the zone's tiers, which change at
  // 19,99 €, so adding one more pizza can move the charge.
  const goodsCents = cartTotals({
    cart,
    orderType,
    // §9.393 - the capability the fields were rendered from, not the slug.
    needsAddress: needs.address,
    deliveryFee: 0,
    discountMode,
    discountValue,
    couponDiscount: coupon?.discountCents ? coupon.discountCents / 100 : 0,
    // §9.182 — the deposits `OrderCreationService.surchargeTotal` will charge. Omitted here, the
    // screen quoted a price the till exceeded.
    depositCents: cartDepositCents(cart, Array.isArray(products) ? products : []),
  }).subtotalCents
  const feeCents = feeCentsFor(zone, goodsCents)

  const totals = cartTotals({
    cart,
    orderType,
    needsAddress: needs.address,
    // Wired at last. It was a literal `0`, and the live zones charge 3,00–5,00 €.
    // §9.161 — with no zone the dispatcher's own figure is the only one there is.
    /* §9.420 - **an existing order keeps the fee it was charged.**
     *
     * On create the zone decides. On an edit it must not: `feeCentsFor` answers the zone's fee
     * *today*, and a dispatcher adding a drink to last week's order would silently re-price its
     * delivery. The seeded box above holds what the order actually carries, and it is still a box -
     * so changing the fee stays possible, deliberately, which is the only way `deliveryCost` is
     * ever sent.
     *
     * Found by `new-order.spec.ts:1448`: adding a second helping began sending `deliveryCost`
     * alongside `newItems`, because the recomputed fee disagreed with the stored one. The spec was
     * right and the first version of this fix was wrong. */
    deliveryFee:
      editing !== null
        ? manualDeliveryFee
        : couriersDeliver
          ? (courierFeeCents ?? 0) / 100
          : feeCents !== null
            ? feeCents / 100
            : manualDeliveryFee,
    discountMode,
    discountValue,
    couponDiscount: coupon?.discountCents ? coupon.discountCents / 100 : 0,
    // §9.744 amended - on an edit nobody touched the box of, the figure the save leaves on the order, taken as given.
    manualDiscountCentsOverride: untouchedDiscount,
  })

  /**
   * §9.120, the owner's point 6 - *"pridam pizzu, neponukne darcek 1+1 za 3€"* - and §9.697, owner #21 / O75,
   * *Darčeky podľa sumy* at the till: *"nad 70€ kofola zdarma"*.
   *
   * **§9.762, owner O124 - offered to whoever holds *Uplatnenie darčeka* (`mayApplyGift`)**, which the server asks
   * when the order is saved (BACKEND.md §10A.416); without it the cart reads no rule and offers no gift. An amount gift
   * is offered once **the food after discounts, without delivery** reaches the rule's amount - the subtotal less the
   * manual and coupon discounts, which is the `discount` this order is saved with and the figure the server checks
   * (§10A.354). `useCartGifts` holds the rules and says why nothing is taken off on rules that were not read.
   */
  const foodCents = Math.max(0, totals.subtotalCents - totals.manualDiscountCents - totals.couponDiscountCents)
  const { offers: giftOffers, prunedCart } = useCartGifts(cart, foodCents, mayApplyGift)
  // *"if the order drops below, the gift is removed"* - a line or a discount taken off is the usual way there.
  // **Adjusted while rendering rather than in an effect** - React's own pattern for state that follows other state,
  // and the one lint allows: the cart is written only when a gift is stale, and the next render finds none, so this
  // settles in one pass.
  if (prunedCart !== cart) setCart(prunedCart)

  const applyCoupon = async () => {
    const code = couponCode.trim()
    if (!code) return

    setCoupon({ loading: true, code })
    try {
      const validated = await postCouponsValidate({
        code,
        orderAmount: totals.subtotalCents / 100,
      })
      if (validated.valid !== true) {
        setCoupon({ error: tt('Kupón neplatí pre túto objednávku') })
        return
      }
      setCoupon({
        code: validated.code ?? code,
        discountCents: parseMoneyToCents(validated.discountAmount),
      })
    } catch (caught) {
      setCoupon({ error: apiErrorMessage(caught, tt('Kupón neexistuje')) })
    }
  }

  /** What the screen refuses to send, and why. */
  const scheduledLabel = scheduledLabelOf(preorderDate, preorderTime)
  /**
   * The lunch menu follows the delivery day — legacy `new-order.tsx:459`.
   *
   * A lunch line carries the day it was authored for. Without this the builder would go on showing
   * today's meals while the order is for Thursday, and Wednesday's *halušky* would be added to a
   * Thursday delivery without either screen saying so.
   *
   * Legacy does it in an effect on `[isPreorder, scheduledDate]`; here it is in the two handlers that
   * are the only ways those change. Same behaviour, and it does not set state during a render —
   * `react-hooks` rejects that as a cascading render, and it is right to.
   */
  /**
   * Typing a telephone number looks the customer up, 600 ms after the typing stops — legacy
   * `new-order.tsx:246`.
   *
   * Debounced rather than per keystroke because the endpoint is a query per call, and cleared first so
   * a badge from the previous number never sits over a new one. Nothing already typed is overwritten;
   * `filledFrom` holds that rule and is tested on its own.
   */
  const changePhone = (value: string) => {
    setCustomerPhone(value)
    setAutofilledFrom(null)
    setPastAddresses([])
    if (lookupTimer.current !== null) clearTimeout(lookupTimer.current)
    if (!worthLookingUp(value)) return

    lookupTimer.current = setTimeout(() => {
      setLooking(true)
      getCustomersLookup({ phone: value.trim().replace(/\s+/g, '') })
        .then((payload) => {
          const match = matchOf(payload)
          if (match === null) return
          setPastAddresses(match.pastAddresses)
          setAutofilledFrom(autofilledLabel(match, tt('databázy')))
          // One functional update per field, because the three rules are independent and each has to
          // see what is in *its* box when the answer lands — not what was there when the request left.
          // Somebody who types the name while the lookup is in flight keeps it.
          setCustomerName((typed) => (typed === '' ? match.name : typed))
          setCustomerEmail((typed) => (typed === '' ? match.email : typed))
          setCustomerAddress(
            (typed) => filledFrom(match, { name: '', email: '', address: typed }).address,
          )
        })
        // A lookup that fails is not an error the person taking the order can act on — legacy swallows
        // it too. They type the name themselves, which is what they were doing before this existed.
        .catch(() => undefined)
        .finally(() => setLooking(false))
    }, 600)
  }

  const scheduleFor = (isOn: boolean, date: string) => {
    setPreorderOn(isOn)
    setPreorderDate(date)
    setLunchDate(isOn && date !== '' ? date : toDateParam(new Date()))
  }

  // Legacy disables the button on this rather than warning, and it is right to: the kitchen would be
  // sent a dish that is not on that day's menu.
  const menuMismatch = lunchMismatch(preorderOn, preorderDate, lunchDate)

  const blockedReason = (): string | null => {
    /* §9.432, and **first** of all the checks.
     *
     * The owner: *"chcela dohodiť nejaké veci do nej … vytvorilo druhu obj kde bola len mirinda"*.
     * An order named by `edit=` that has not loaded leaves this screen with an empty cart and a
     * save that creates rather than patches - so the refusal has to come before *Košík je prázdny*,
     * which would otherwise be the message for a state that has nothing to do with the basket. */
    const notLoaded = editLoadProblem(editId, editing, { failed: editFailed })
    if (notLoaded !== null) return notLoaded
    if (cart.length === 0) return tt('Košík je prázdny')
    if (needs.table && !tableName.trim()) return tt('Zadajte číslo stola')
    if (needs.address && !customerAddress.trim()) return tt('Zadajte adresu doručenia')
    if (editing !== null && editRefusal(editing) !== null) return editRefusal(editing)
    if (needs.phone && !customerPhone.trim()) return tt('Zadajte telefónne číslo')
    const waiterMissing = waiterProblem(needs.table, waiterList, waiterId)
    if (waiterMissing !== null) return waiterMissing
    // Legacy `new-order.tsx:902`. A časenka switched on with nothing typed would send `isPreorder` and
    // no time, which the kitchen reads as due now — the opposite of what was asked for.
    if (preorderOn && (preorderDate === '' || preorderTime === ''))
      return tt('Pri časenke zadajte dátum a čas doručenia.')
    if (menuMismatch) return tt('Obedové menu nesúhlasí s Časenkou')
    // Only when taking a new order. An order being edited already carries its delivery charge, and a
    // second helping does not buy a second drive — demanding a zone here would block every edit.
    const delivery = deliveryProblems(needs.address && editing === null, zone, goodsCents)
    if (delivery.length > 0) return delivery[0] ?? null
    return null
  }

  /**
   * What the save will send, computed at render - §9.383.
   *
   * **The banner needs it too.** `editSummary` takes the scalars so it can say *a note changed* or
   * *the channel changed*, and the call site below passed the cart alone - so the sentence §9.374
   * rewrote still read *Zatiaľ nič nepridané* over a correction. One pure call, two readers.
   */
  const editingScalars: EditScalars =
    editing === null
      ? {}
      : editScalars(editing, {
          type: orderType,
          // The channel's capability, never its slug - §10A.87.
          seatsGuests: needs.table,
          tableName,
          customerName,
          customerPhone,
          customerEmail,
          customerAddress,
          // §9.693, FB-1003 - sent only when it moved: the new point, or a clear when the address lost it.
          deliveryPoint,
          notes,
          // §9.420, FB-747 - the *Zľava* control has been live since this page was written and its
          // value stopped here. The whole figure for `orders.discount`, and the mode, the typed
          // number and the operator so the manual half is recorded the way the create path records it.
          discountCents: totals.manualDiscountCents + totals.couponDiscountCents,
          discountMode,
          discountValue,
          operatorName: user?.name ?? null,
          // §9.744 amended - untouched, nothing about the discount is sent but a coupon applied in this edit.
          discountTouched: editsTheDiscount,
          couponCents: totals.couponDiscountCents,
          deliveryCents: totals.deliveryCents,
          totalCents: totals.totalCents,
          paymentMethod,
          waiterId,
          isPreorder: preorderOn,
          scheduledAt: preorderOn ? scheduledAtOf(preorderDate, preorderTime) : '',
        })

  /**
   * What the save sends, and what it does with the answer - §9.620, owner R2.
   *
   * **Named because it is sent twice now.** A patch the server refuses for want of a decision about
   * a live fiscal document is re-sent from `FiskalDecisionDialog` - unchanged after a storno, or with
   * `keepsFiskalReason` - and two copies of the success handling would be two chances to forget
   * §9.433's second invalidation, which is the one that stopped the next edit reading a missing line
   * as a deletion.
   *
   * **`keepsFiskalReason` rides on the body because `PatchOrdersOrderIdBody` is
   * `{ [key: string]: unknown }`**, so the key is held to the server's own reader by a backend guard
   * rather than by the compiler - §9.620's own note on that.
   */
  const sendEdit = (orderId: number, patch: PatchOrdersOrderIdBody, keepsFiskalReason?: string) => {
    const data = keepsFiskalReason === undefined ? patch : { ...patch, keepsFiskalReason }
    updateOrder.mutate(
      { orderId, data },
      {
        onSuccess: () => {
          setFiskalQuestion(null)
          void queryClient.invalidateQueries({ queryKey: getGetOrdersQueryKey() })
          // §9.433, FB-756 - **and the order itself.** Only the list was invalidated, so the
          // detail this screen edits from stayed on its pre-save version; the next edit seeded
          // the cart from that and the save read the missing line as a deletion.
          if (editId !== null) {
            void queryClient.invalidateQueries({ queryKey: getGetOrdersOrderIdQueryKey(editId) })
          }
          setSubmitted(true)
          setTimeout(() => navigate(savedOrderRoute()), CONFIRMATION_MS)
        },
        onError: (caught) => {
          /* §9.620 - **the question, on the server's own code.** §10A.315 answers
           * `FISKAL_DOCUMENT_LIVE` on that one 400 so this branch needs no Slovak: the same save can
           * answer §10A.300's unknown status and §10A.282's payment-status refusal, and matching a
           * message is §9.610's defect. The patch is held so either route can re-send it. */
          if (needsAFiskalDecision(caught)) {
            setFiskalQuestion(patch)
            return
          }
          setFiskalQuestion(null)
          toast({
            variant: 'destructive',
            title: tt('Chyba'),
            description: apiErrorMessage(caught, tt('Objednávku sa nepodarilo upraviť.')),
          })
        },
      },
    )
  }

  const submit = () => {
    const blocked = blockedReason()
    if (blocked) {
      toast({ variant: 'destructive', title: tt('Chýbajú údaje'), description: blocked })
      return
    }

    // Editing writes a **round**, not a replacement: `items` would delete the ticket the kitchen is
    // cooking from and re-insert it, while `newItems` joins the round on the pass or starts one.
    if (editing !== null) {
      // §9.374, FB-668 - **what changed, not only what was added.** The old call sent
      // `editPayload(cart)` and that was `newItems` or nothing, so a correction, a removal, a note
      // and a payment method all reached the server as silence. The scalars are sent only where they
      // differ from what was loaded: a patch that names a field rewrites it, and re-sending an
      // unchanged note would stamp an edit on an order nobody edited.
      //
      // §9.383, FB-681 - **and it was two fields until the owner asked for a third.** *"changing
      // objednávka from reštaurácia to osobný odber - its not possible"*: the chips were live, the
      // save dropped them, and the screen said *Objednávka uložená*. The same silence covered the
      // customer's name, phone, e-mail and address, the table, the waiter and the časenka, so the
      // diff moved into `editScalars` where it can be tested field by field.
      const scalars = editingScalars
      // §9.420 - the payable figure the screen shows. Without this argument `editPayload` falls back
      // to the goods, which is what wrote 17,00 € over a delivery order's total.
      const patch = editPayload(cart, editing, scalars, totals.totalCents)
      if (patch === null) {
        toast({ title: tt('Nič sa nezmenilo'), description: editSummary(cart, editing, scalars) })
        return
      }
      sendEdit(editing.id, patch)
      return
    }

    const payload = buildOrderPayload({
      cart,
      totals,
      orderType,
      paymentMethod,
      tableName,
      // §9.393 - the same two answers the fields above were rendered from. Naming the order by the
      // literal `restaurant` collected a table number on `na_sekeru` and then dropped it.
      seatsGuests: needs.table,
      needsAddress: needs.address,
      // §9.40 — only where the channel seats people. A delivery has a driver, and sending a waiter
      // with one would put the takings against somebody who never touched the order.
      ...(needs.table && waiterId !== null ? { employeeId: waiterId } : {}),
      customerName,
      customerPhone,
      customerAddress,
      // §9.693, FB-1003 - the point the address was picked at, which this call never passed.
      coordinates: deliveryPoint,
      customerEmail,
      notes,
      discountMode,
      discountValue,
      couponCode: coupon?.code ?? null,
      operatorName: user?.name ?? null,
      ...(preorderOn ? { isPreorder: true, scheduledAt: scheduledAtOf(preorderDate, preorderTime) } : {}),
    })

    createOrder.mutate(
      { data: payload as unknown as Parameters<typeof createOrder.mutate>[0]['data'] },
      {
        onSuccess: (created) => {
          void queryClient.invalidateQueries({ queryKey: getGetOrdersQueryKey() })
            // §9.433, FB-756 - **and the order itself.** Only the list was invalidated, so the
            // detail this screen edits from stayed on its pre-save version; the next edit seeded
            // the cart from that and the save read the missing line as a deletion.
            if (editId !== null) {
              void queryClient.invalidateQueries({ queryKey: getGetOrdersOrderIdQueryKey(editId) })
            }

          // §9.212 — *Do ruky*: the person who took the order is the person about to take the
          // money, so the payment dialog opens here rather than after a trip to a board they would
          // have to come back from. The dialog is the restaurant one, unchanged: it already holds
          // the fiškál choice and the cash/card choice, which is what *ako v reštauračnom toku*
          // asks for.
          // `POST /orders` answers with the order itself — `OrderDetail`, which carries the id, the
          // channel, the status, the lines and the customer's name: every field the dialog reads.
          const order = created as unknown as OrderListItem | null
          if (settlesHere && order?.id != null) {
            setSettling(order)
            return
          }

          setSubmitted(true)
          // Legacy `new-order.tsx:978`. A časenka is not on the dispatch board yet, so landing there
          // would show the person who just took it a screen their order is deliberately absent from.
          setTimeout(() => navigate(savedOrderRoute({ preorder: preorderOn })), CONFIRMATION_MS)
        },
        onError: (caught) =>
          toast({
            variant: 'destructive',
            title: tt('Chyba'),
            description: apiErrorMessage(caught, tt('Nepodarilo sa vytvoriť objednávku.')),
          }),
      },
    )
  }

  // §9.212 — the order is taken and the money is next. Dismissing without paying goes to the board
  // like everything else: an unsettled order is not a dead end, and the board is where it is looked
  // after.
  if (settling !== null) {
    return (
      <div data-settling-order={settling.id}>
        <PaymentDialog
          order={settling}
          // §9.740 - the till asks who took the money, where the channel needs it.
          closer="asked"
          onClose={() => navigate(savedOrderRoute())}
          onPaid={() => {
            void queryClient.invalidateQueries({ queryKey: getGetOrdersQueryKey() })
            // §9.433, FB-756 - **and the order itself.** Only the list was invalidated, so the
            // detail this screen edits from stayed on its pre-save version; the next edit seeded
            // the cart from that and the save read the missing line as a deletion.
            if (editId !== null) {
              void queryClient.invalidateQueries({ queryKey: getGetOrdersOrderIdQueryKey(editId) })
            }
            navigate(savedOrderRoute())
          }}
          /* §9.464 - the zľava was edited, not the close. **No navigation**: the operator is still
           * settling. `settling` is this screen's own state and no query, so the dialog's own override
           * is what keeps the bill right; these invalidations are for the archive and the detail. */
          onOrderChanged={() => {
            void queryClient.invalidateQueries({ queryKey: getGetOrdersQueryKey() })
            if (editId !== null) {
              void queryClient.invalidateQueries({ queryKey: getGetOrdersOrderIdQueryKey(editId) })
            }
          }}
        />
      </div>
    )
  }

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <CheckCircle2 className="w-16 h-16 text-emerald-500" />
        <p className="text-xl font-bold">
          <T>Objednávka vytvorená</T>
        </p>
        <p className="text-sm text-muted-foreground">
          <T>Presmerovávam na nástenku…</T>
        </p>
      </div>
    )
  }

  /* §9.744, owner O114 - an edit without `edit_order` is refused by the server (BACKEND.md §10A.397), so the screen
     says so rather than drawing an editor whose save cannot land - a control offered and then refused is the defect
     §9.661 names. After every hook, because a hook below an early return is one React skips. */
  const editRefused = editorRefusal(editId, mayEditOrder)
  if (editRefused !== null) {
    return (
      <div className="space-y-4">
        <PageHeader title={tt('Nová objednávka')} icon={<ShoppingCart className="w-5 h-5" />} />
        <div className="rounded-2xl border border-border bg-card p-4 space-y-1.5" data-edit-refused={editId ?? 0}>
          <p className="text-sm font-bold">
            <T>Úprava objednávky</T> {editing === null ? '' : `#${editing.orderNumber}`}
          </p>
          <p className="text-[11px] text-amber-500">{editRefused}</p>
        </div>
      </div>
    )
  }

  return (
    /* §9.313, owner P4 - *"make sure in nová objednávka, categories are also sticky with
     * rozvoz/reštaurácia basically these kanály and also zákazník"*.
     *
     * **The whole page is bounded to the window on desktop, and only the products scroll.** That is
     * legacy's own design (`new-order.tsx:1118`, `xl:h-[calc(100vh-12rem)]`), and the reason it is
     * portable here after FB-490 rejected a fixed offset is that **this** offset is the app chrome
     * alone: measured at 56 px of header plus 24 px of page padding, identical on `/new-order` and
     * `/dispatch`, and independent of the in-page blocks. What FB-490 refused was an offset that had
     * to guess the *content* above the grid - the channel chips wrap at eight channels and *Zákazník*
     * is absent on a table order - and flex handles both of those on its own.
     *
     * **Sticky positioning could not do this**: the chips and *Zákazník* sit above the grid while the
     * category row sits inside its left column, so they are in different parents and one sticky
     * container cannot span them. Stacking two would need the first one's height as a constant, which
     * is the very thing that varies.
     *
     * `dvh` rather than `vh` because the manager runs on tablets, where the browser's own chrome
     * changes the viewport as it scrolls. Below `lg` nothing is bounded and the page scrolls as it
     * always has. */
    <div className="space-y-4 lg:flex lg:h-[calc(100dvh-6.5rem)] lg:min-h-0 lg:flex-col lg:space-y-4 lg:overflow-hidden">
      <PageHeader title={tt('Nová objednávka')} icon={<ShoppingCart className="w-5 h-5" />} />

      {/* §9.24 - legacy's row: equal cells filling the width, each label centred in its own. This was
        * `flex flex-wrap`, which sized every button by the length of its channel's name and pushed the
        * lot against the left margin. `auto-fit` rather than legacy's `flex-1` because §9.5 made the
        * count the franchise's own: three across at three, and equal cells on a second row at eight,
        * where `flex-1` would give eight slivers. */}
      <div
        className="grid gap-3"
        data-channel-row
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(11rem, 1fr))' }}
      >
        {channels.map((channel) => {
          const Icon = iconFor(channel)
          return (
            <button
              key={channel.slug}
              type="button"
              data-order-channel={channel.slug}
              data-order-channel-active={orderType === channel.slug ? 'yes' : 'no'}
              onClick={() => setOrderType(channel.slug)}
              // §9.38 — the channel's own colour, and only while it is not the one selected. The
              // colour tells four channels apart at a glance; the primary highlight says which one
              // this order is on, and mid-order that is the answer a waitress needs. §9.15's rule.
              style={
                orderType === channel.slug || channel.colour === null
                  ? undefined
                  : categoryChipStyle(channel.colour)
              }
              data-order-channel-colour={channel.colour ?? ''}
              className={cn(
                'flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold border transition-all',
                orderType === channel.slug
                  ? 'bg-primary/10 border-primary text-primary'
                  : channel.colour === null
                    ? 'bg-card border-border text-muted-foreground hover:text-foreground'
                    : 'bg-card',
              )}
            >
              <Icon className="w-4 h-4" /> {channel.name}
            </button>
          )
        })}
      </div>

      {/* §9.14 - legacy's position: the main column, under the order types and above the products.
        * A table order identifies itself by its table, so the card is for the channels that need a
        * person. */}
      {!needs.table && (
        <CustomerPanel
          phone={customerPhone}
          onPhoneChange={changePhone}
          name={customerName}
          onNameChange={setCustomerName}
          email={customerEmail}
          onEmailChange={setCustomerEmail}
          address={customerAddress}
          onAddressChange={addressTyped}
          onAddressResolved={(place) => {
            // The owner's request of 2026-08-03: *zóna by sa mala vybrať automaticky podľa adresy*.
            // §9.119 - a resolved point is a stronger answer than a name in a string, so it settles
            // the zone and the name match stops second-guessing it. §9.801 - by the server's rule: while
            // the point stands the zone is `pointZoneId`, derived; this records it for when typing clears
            // the point, so the zone stays what the point said rather than reverting to an older guess.
            zoneChosenByHand.current = true
            setZoneId(
              zoneForPoint({ lat: place.lat, lng: place.lon }, zoneData, {
                address: place.formattedAddress !== '' ? place.formattedAddress : customerAddress,
                depot,
              }),
            )
            // §9.693, FB-1003 - and the point is kept for the order, which is what the driver navigates to.
            setDeliveryPoint(usablePoint({ lat: place.lat, lng: place.lon }))
          }}
          onAddressUnresolved={(address) => {
            // §9.372 - the pick changed the address and its coordinates never arrived, so there is
            // no resolved point behind the zone any more. §9.119's rule is that *a resolved point is
            // a stronger answer than a name in a string*; with no point, the name is the strongest
            // answer there is, so the guess is re-opened for the address that was actually picked.
            //
            // Without this the zone stays the one the *previous* address resolved into:
            // `zoneChosenByHand` is still set from that resolve, so `addressTyped` - which ran two
            // lines earlier inside `pick` - returned without guessing.
            zoneChosenByHand.current = false
            addressTyped(address)
          }}
          showAddress={needs.address}
          looking={looking}
          autofilledFrom={autofilledFrom}
          pastAddresses={pastAddresses}
          // §9.371, FB-659 - a picked address is a typed address somebody did not have to type.
          // This was `setCustomerAddress`, which set the text and left the zone unguessed, so the
          // dispatcher was asked for a zone the screen could have worked out. `addressTyped` still
          // respects `zoneChosenByHand`, so it cannot override a choice they made themselves.
          onPickAddress={addressTyped}
          // §9.694 - the map under the address. A dropped pin is the operator's own answer to *where*: it
          // becomes the order's point and the zone follows it, whatever the text guessed. The text itself
          // is replaced only when the place has a name, and directly - `addressTyped` would clear the point.
          deliveryPoint={deliveryPoint}
          depot={depot}
          zones={pinMapZones}
          onPinMoved={(point) => {
            setDeliveryPoint(point)
            zoneChosenByHand.current = true
            setZoneId(zoneAfterPin(point, zoneData, { address: customerAddress, depot }))
          }}
          onPinAddress={setCustomerAddress}
        />
      )}
      {/* §9.787, owner O140 - a blocked phone or e-mail is said, not refused: the person on the phone decides. */}
      {!needs.table && blockMatches.length > 0 && (
        <div
          className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400 space-y-0.5"
          data-blocked-warning
        >
          {blockMatches.map((match) => (
            <p key={`${match.kind}-${match.shown}`}>{blockMatchNote(match)}</p>
          ))}
        </div>
      )}

      {editing !== null && (
        <div className="rounded-2xl border border-border bg-card p-4 space-y-1.5" data-editing={editing.id}>
          <p className="text-sm font-bold">
            <T>Úprava objednávky</T> #{editing.orderNumber}
          </p>
          {!mayEdit(editing) ? (
            <p className="text-[11px] text-destructive" data-edit-settled>
              {editRefusal(editing)}
            </p>
          ) : (
            <>
              {lockedNote(editing) !== '' && (
                <p className="text-[11px] text-amber-500" data-edit-locked>
                  {lockedNote(editing)}
                </p>
              )}
              {/* §9.620, owner R2 - **a warning where §9.567 had a refusal.** This was returned from
                * `editRefusal`, so the save was stopped and this sentence was printed in red; the owner
                * asked for the question to come at save time instead. Amber beside the locked-rounds
                * note rather than destructive, because nothing here is refused any more. */}
              {printedWarning(editing) !== '' && (
                <p className="text-[11px] text-amber-500" data-edit-printed>
                  {printedWarning(editing)}
                </p>
              )}
              {/* §9.383 - **the note was written and never rendered.** §9.374 added
                * `paymentMethodLockedNote` for an order whose money came in through CardPay, and
                * nothing drew it: switching the method on one did nothing and said nothing. */}
              {!mayChangePaymentMethod(editing) && (
                <p className="text-[11px] text-amber-500" data-edit-payment-locked>
                  {paymentMethodLockedNote()}
                </p>
              )}
              {/* §9.383 - with `editing` and the scalars, as §9.374 wrote it. The call passed the
                * cart alone, so the sentence read *Zatiaľ nič nepridané* over a correction. */}
              <p className="text-[11px] text-muted-foreground" data-edit-summary>
                {editSummary(cart, editing, editingScalars)}
              </p>
            </>
          )}
        </div>
      )}

      {/* §9.313 - `items-start` is gone: the columns fill the bounded row so each can own its scroll.
        * With it they hugged their own top and the row's height did nothing. */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-4 items-start lg:min-h-0 lg:flex-1 lg:items-stretch">
        <div className="space-y-3 lg:flex lg:min-h-0 lg:flex-col">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                value={productSearch}
                onChange={(event) => setProductSearch(event.target.value)}
                placeholder={tt('Hľadať produkt...')}
                aria-label={tt('Hľadať produkt...')}
                className={cn(FIELD_CLASSES, 'pl-9')}
              />
            </div>
          </div>

          {/* §9.42's "one chip lit" is about *this* row. The size and base pickers inside the pizza
            * panel are the same `CategoryChip`, and one of those being lit is right (§9.45). */}
          <div className="flex flex-wrap gap-1.5" data-category-row>
            <CategoryChip
              // §9.42 — `panel === 'none'`, not `!lunchOpen`. It excluded the daily menu and not the
              // burger box or the pizza builder, so opening either lit *Všetko* beside them: the
              // same two-chips-at-once the owner reported, from the other direction.
              active={activeCategory === null && panel === 'none'}
              onClick={() => chooseCategory(null)}
              label={tt('Všetko')}
              testId="all"
            />
            {/* The daily menu is not in the product catalogue, so without this tab a lunch order taken
              * over the phone had to be found somewhere it does not exist. */}
            {lunchIsOffered(lunch) && (
              <CategoryChip
                active={lunchOpen}
                onClick={() => openPanel('lunch')}
                label={tt('Obedové menu')}
                testId="lunch"
              />
            )}
            {categoryList.map((category) => (
              <CategoryChip
                key={category.id}
                active={activeCategory === category.id}
                onClick={() => chooseCategory(category.id ?? null)}
                label={category.name ?? ''}
                testId={`category-${String(category.id ?? 0)}`}
                colour={categoryColour(category.color)}
              />
            ))}
          </div>

          {/* §9.313, owner P4 - **only this scrolls.** The search row and the category chips above
            * it stay where they are, which is the half of the owner's request that is about the
            * categories; the chips and *Zákazník* above the grid stay because the page itself is
            * bounded. `min-h-0` is load-bearing: without it a flex child refuses to shrink below
            * its content and the scroller never engages. */}
          <div className="lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:pr-1">
          {boxOpen && box !== null ? (
            <div className="space-y-3" data-box-panel={box.id}>
              {box.description !== null && (
                <p className="text-xs text-muted-foreground">{box.description}</p>
              )}
              {box.slots.map((slot) => {
                const choices = choicesForSlot(slot, boxProducts)
                const fixed = fixedForSlot(slot, boxProducts)
                return (
                  <section key={slot.id} className="space-y-1.5" data-box-slot={slot.id}>
                    <h3 className="text-sm font-bold">
                      {slot.label}
                      {slot.required && <span className="text-destructive"> *</span>}
                    </h3>
                    {fixed.map((product) => (
                      <p key={product.id} className="text-[11px] text-muted-foreground">
                        {product.name} - <T>vždy v boxe</T>
                      </p>
                    ))}
                    {choices.length === 0 ? (
                      <p className="text-[11px] text-amber-500" data-box-slot-empty={slot.id}>
                        <T>Pre túto pozíciu nie sú nastavené žiadne produkty.</T>
                      </p>
                    ) : (
                      <select
                        value={boxSelections[slot.id] === undefined || boxSelections[slot.id] === null ? '' : String(boxSelections[slot.id])}
                        onChange={(event) =>
                          setBoxSelections((current) => ({
                            ...current,
                            [slot.id]: event.target.value === '' ? null : Number(event.target.value),
                          }))
                        }
                        aria-label={slot.label}
                        data-box-select={slot.id}
                        className={FIELD_CLASSES}
                      >
                        <option value="">{tt('- nič -')}</option>
                        {choices.map((product) => (
                          <option key={product.id} value={product.id}>
                            {product.name} · {formatCents(product.priceCents)}
                          </option>
                        ))}
                      </select>
                    )}
                  </section>
                )
              })}

              <p className="text-xs text-muted-foreground" data-box-summary>
                {boxSummary(box, boxSelections, boxProducts)}
              </p>
              {boxProblems(box, boxSelections, boxProducts).map((problem) => (
                <p key={problem} className="text-[11px] text-amber-500" data-box-problem>
                  {problem}
                </p>
              ))}
              <button
                type="button"
                data-box-add
                disabled={boxProblems(box, boxSelections, boxProducts).length > 0}
                onClick={() => {
                  const lines = boxLines(box, boxSelections, boxProducts).map((line) => ({
                    slotLabel: line.slot.label,
                    product: {
                      id: line.product.id,
                      name: line.product.name,
                      price: line.product.priceCents / 100,
                      categoryName: box.name,
                    },
                  }))
                  // Seeded from the cart, not a clock: two boxes in one order differ by their length.
                  updateCart((current) =>
                    addBoxLines(current, boxGroupId(`${box.id}-${current.length}`), box.name, lines),
                  )
                  setBoxSelections({})
                }}
                className={cn('w-full min-h-[48px] rounded-xl', BUTTON_PRIMARY)}
              >
                <T>Pridať box do košíka</T>
              </button>
            </div>
          ) : lunchOpen ? (
            <div className="space-y-3" data-lunch-panel>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-lunch-prev
                  aria-label={tt('Predchádzajúci deň')}
                  onClick={() => setLunchDate((current) => shiftDate(current, -1))}
                  className="min-h-[40px] px-3 rounded-xl border border-border text-sm font-semibold"
                >
                  ‹
                </button>
                <input
                  type="date"
                  value={lunchDate}
                  onChange={(event) => setLunchDate(event.target.value)}
                  aria-label={tt('Deň obedového menu')}
                  data-lunch-date
                  className={cn(FIELD_CLASSES, 'flex-1')}
                />
                <button
                  type="button"
                  data-lunch-next
                  aria-label={tt('Nasledujúci deň')}
                  onClick={() => setLunchDate((current) => shiftDate(current, 1))}
                  className="min-h-[40px] px-3 rounded-xl border border-border text-sm font-semibold"
                >
                  ›
                </button>
              </div>

              {lunchSections.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center" data-lunch-empty>
                  {emptyDayNote(lunch, lunchDate)}
                </p>
              ) : (
                lunchSections.map((section) => (
                  <section key={section.type} className="space-y-2" data-lunch-section={section.type}>
                    <h3
                      className={cn(
                        'text-sm font-bold flex items-center gap-1.5',
                        section.unnamed ? 'text-amber-500' : 'text-foreground',
                      )}
                    >
                      {/* §9.91 - the same glyph the customer sees over this section. */}
                      <CategoryIcon
                        icon={lunchSectionIconName(section.label, categoryList)}
                        className="w-3.5 h-3.5"
                      />
                      {section.label}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {section.meals.map((meal) => {
                        const line = lunchCartLine(meal)
                        return (
                          <button
                            key={meal.id}
                            type="button"
                            data-lunch-meal={meal.id}
                            onClick={() =>
                              updateCart((current) =>
                                // §9.178 — tagged as a day's-menu line so the cart draws them in one
                                // box. Client-only: `orderLinePayload` never reads it.
                                addProduct(current, {
                                  id: line.productId,
                                  name: line.name,
                                  price: line.unitPriceCents / 100,
                                  categoryName: section.label,
                                }).map((entry) =>
                                  entry.productId === line.productId
                                    ? // §10A.88 - `lunchGroup` stays client-only (it decides the
                                      // cart's box); `lunchMealType` is what travels, and it is
                                      // what lets the kitchen box the day's menu without mistaking
                                      // a custom pizza for one.
                                      { ...entry, lunchGroup: true, lunchMealType: section.type }
                                    : entry,
                                ),
                              )
                            }
                            className="text-left bg-card border border-border rounded-xl p-3 hover:border-primary/50 hover:bg-muted/40 transition-all"
                          >
                            <p className="text-sm font-semibold">{line.name}</p>
                            {/* §9.401 - the linked product's popis, never the stored copy. This
                              * screen was the fourth reader of it and the guard in
                              * `lunch-description-source.test.ts` is what found it. */}
                            {lunchMealDescription(meal, products ?? []) !== '' && (
                              <p className="text-[11px] text-muted-foreground line-clamp-2">
                                {lunchMealDescription(meal, products ?? [])}
                              </p>
                            )}
                            <p className="text-xs font-bold text-primary pt-1">{mealPriceNote(meal)}</p>
                          </button>
                        )
                      })}
                    </div>
                  </section>
                ))
              )}
            </div>
          ) : productsFailed ? (
            /* §9.633, FB-948 - **before the empty state, because `visibleProducts` is empty on a failure too**
             * and drawn second this branch would never run (§9.547's ordering).
             *
             * The sentence below it is *Skúste zmeniť filtre* - an instruction, and a true one when a filter
             * matched nothing, which is why this adds a state rather than rewording that one. The owner's menu
             * holds 177 active orderable products. */
            <ReadFailedBlock
              marker="products"
              title={tt('Produkty sa nepodarilo načítať')}
              onRetry={() => void refetchProducts()}
            >
              <T>Neznamená to, že menu je prázdne - nie je to ani vo filtroch. Skúste to znova.</T>
            </ReadFailedBlock>
          ) : visibleProducts.length === 0 ? (
            <div className="bg-card border border-border rounded-2xl p-10">
              <EmptyState
                icon={<Search className="w-10 h-10" />}
                title={tt('Žiadne produkty')}
                description={tt('Skúste zmeniť filtre')}
              />
            </div>
          ) : (
            <div className="space-y-4" data-product-grid>
              {/* §9.86 - where legacy draws them: two tiles at the top of the grid, inside the pizza
                * category. AGOS had them as chips out in the category bar, which is why the owner
                * could not find them *in pizza*. The chips are still there; these are the pair
                * `new-order.tsx` has always opened. */}
              {/* §9.88 - the box belongs to the category its config names, and is opened from inside
                * it: the owner, on seeing it as a chip, *"When I click on burger, burger box category
                * is shown, but I want it inside of Burgre, not as another category"*. */}
              {activeCategory !== null &&
                boxes.map((offered) => (
                <button
                  key={offered.id}
                  type="button"
                  data-builder="box"
                  data-box-offer={offered.id}
                  onClick={() => {
                    setChosenBoxId(offered.id)
                    openPanel('box')
                  }}
                  className="w-full flex items-center gap-2 p-3 rounded-xl border-2 border-dashed border-green-400/60 text-left hover:bg-green-50/30 transition-all"
                >
                  <Boxes className="w-4 h-4 text-green-600 shrink-0" />
                  <div>
                    <div className="font-semibold text-green-700 text-xs leading-tight">
                      {offered.name}
                    </div>
                    {offered.description !== null && offered.description !== '' && (
                      <div className="text-[10px] text-muted-foreground">{offered.description}</div>
                    )}
                  </div>
                </button>
              ))}
              {pizzaCategoryOpen && (
                <div className="grid grid-cols-2 gap-2" data-pizza-builders>
                  <button
                    type="button"
                    data-builder="pizza"
                    onClick={() => openPanel('pizza')}
                    className="flex items-center gap-2 p-3 rounded-xl border-2 border-dashed border-primary/50 text-left hover:bg-primary/5 transition-all"
                  >
                    <Pizza className="w-4 h-4 text-primary shrink-0" />
                    <div>
                      <div className="font-semibold text-primary text-xs leading-tight">
                        <T>Vlastná pizza</T>
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        <T>Zostaviť podľa výberu</T>
                      </div>
                    </div>
                  </button>
                  {isKomboEnabled(pizzaConfig) && komboPizzas.length > 0 && (
                    <button
                      type="button"
                      data-builder="kombo"
                      onClick={() => setKomboOpen(true)}
                      className="flex items-center gap-2 p-3 rounded-xl border-2 border-dashed border-orange-400/60 text-left hover:bg-orange-50/30 transition-all"
                    >
                      <Pizza className="w-4 h-4 text-orange-600 shrink-0" />
                      <div>
                        <div className="font-semibold text-orange-700 text-xs leading-tight">
                          Kombo pizza
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          <span data-kombo-tile-count>{komboCountLabel(komboCount)}</span> pizza{' '}
                          {komboSizeCm(pizzaConfig)}cm
                        </div>
                      </div>
                    </button>
                  )}
                </div>
              )}
              {/* §9.334 - `key={group.categoryId ?? 'nezaradene'}` gave all five pizza sections the
                  same key, and reconciliation duplicated four of them: 26 extra tiles. */}
              {productGroups.map((group) => (
                <div key={groupKey(group)}>
                  {group.title !== '' && (
                    <p
                      className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2"
                      data-product-group={group.categoryId ?? ''}
                    >
                      <T>{group.title}</T>
                    </p>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2">
              {group.products.map((product) => {
                const inCart = quantityOf(cart, product.id ?? 0)
                const gramaz = gramazFor(product)
                const description = product.description ?? null
                const sizes = productSizes(product as unknown as SizedProduct)
                // §9.156 — the kitchen has run out. `!== false` so a payload that predates the
                // field still sells, which is the same tolerance the customer app applies.
                const notOrderable = (product as { orderable?: boolean }).orderable === false
                return (
                  <button
                    key={product.id}
                    type="button"
                    data-orderable={notOrderable ? 'no' : 'yes'}
                    title={notOrderable ? tt('Momentálne nedostupné') : undefined}
                    onClick={() => {
                      // §9.158 — **warned, not blocked.** §9.156 disabled this tile, which was right
                      // while the server refused every path; §10A.79 gives staff the override, and a
                      // screen that forbids what the till is allowed to do is the same bug inverted.
                      // The badge and the dimming stay so nobody adds one by accident.
                      if (notOrderable) {
                        toast({
                          title: tt('Momentálne nedostupné'),
                          description: product.name ?? '',
                        })
                      }
                      // §9.85 — a sized product is added by its size chips; the tile itself adds
                      // nothing, because there is no one price to add it at.
                      if (offersSizeChoice(sizes)) return
                      const addable = {
                        id: product.id ?? 0,
                        name: product.name ?? '',
                        price: product.price,
                        categoryName: product.categoryName,
                      }
                      // §9.713 - the identity of the line this add lands on, so the prompt below can put what is
                      // chosen under it. Only where the prompt can open; every other add is unchanged.
                      const parentLineId = asksAboutAddons ? parentLineIdFor(cart, addable) : null
                      updateCart((current) =>
                        addProduct(
                          current,
                          addable,
                          // §9.41 — what makes the toppings button appear on this line. The flag and
                          // the button both existed; nothing ever set the one or passed the other.
                          {
                            isPizza: isPizzaProduct(product),
                            ...(parentLineId === null ? {} : { lineId: parentLineId }),
                          },
                        ),
                      )
                      /* §9.657, owner O44 - **add first, ask second**, which is `addons.ts`'s own rule and
                         matters more on a till than on a phone: a waiter mid-order with a queue must never be
                         stopped by a modal, and the product they pressed is in the order whatever they do with
                         the question. The failure is swallowed for the same reason - a line already in the
                         basket must not be undone by a lookup. Plain catalogue products only, which is the
                         customer app's own scope (`addPlain`): a sized product has returned above, and a pizza,
                         a box or a day's menu meal is composed rather than picked. */
                      if (asksAboutAddons && product.id !== undefined) {
                        offerUpsellFor({ id: product.id, name: product.name ?? '' }, parentLineId)
                      }
                    }}
                    className={cn(
                      'relative text-left bg-card border border-border rounded-xl p-3 transition-all',
                      notOrderable
                        ? 'opacity-50 grayscale hover:border-amber-500/50'
                        : 'hover:border-primary/50 hover:bg-muted/40',
                    )}
                  >
                    {notOrderable && (
                      <span
                        data-unavailable-badge
                        className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded-full bg-zinc-900/85 text-white text-[9px] font-bold uppercase tracking-wide"
                      >
                        <T>Nedostupné</T>
                      </span>
                    )}
                    <p className="text-sm font-semibold text-foreground line-clamp-2">
                      <T>{product.name ?? ''}</T>
                      {/* §9.82 - the §9.63 rule, unchanged: suppressed where the name already says
                        * it, which is 32 of the 113 products that carry an amount. */}
                      {gramaz !== null && (
                        <span
                          className="ml-1.5 text-[11px] font-bold text-muted-foreground align-middle"
                          data-product-gramaz
                        >
                          {gramaz}
                        </span>
                      )}
                    </p>
                    {description !== null && description !== '' && (
                      <p
                        className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5"
                        data-product-description
                      >
                        {description}
                      </p>
                    )}
                    {/* §9.85 - a chip per size where the product has more than one: its own price,
                      * its own weight, its own tap. One price on a product with two could only ever
                      * add the wrong one, and the server would refuse or re-price it. */}
                    {offersSizeChoice(sizes) ? (
                      <div className="mt-1 space-y-1" data-product-sizes={product.id}>
                        {sizes.map((size) => (
                          <span
                            key={size.label}
                            role="button"
                            tabIndex={0}
                            data-product-size={size.label}
                            onClick={(event) => {
                              event.stopPropagation()
                              addSized(product, size)
                            }}
                            onKeyDown={(event) => {
                              if (event.key !== 'Enter' && event.key !== ' ') return
                              event.preventDefault()
                              event.stopPropagation()
                              addSized(product, size)
                            }}
                            className="flex items-center justify-between gap-2 rounded-lg bg-muted/50 border border-border px-2 py-1 text-[11px] hover:border-primary/50"
                          >
                            <span className="font-bold">{size.label}</span>
                            {sizeWeightText(size) !== null && (
                              <span className="text-muted-foreground" data-size-weight>
                                {sizeWeightText(size)}
                              </span>
                            )}
                            <span className="font-bold text-primary" data-size-price>
                              {formatCents(size.priceCents)}
                            </span>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm font-bold text-primary mt-1">
                        {formatCents(parseMoneyToCents(product.price))}
                      </p>
                    )}
                    {inCart > 0 && (
                      <span className="absolute top-2 right-2 min-w-[20px] h-5 px-1 rounded-full bg-primary text-primary-foreground text-[11px] font-black flex items-center justify-center">
                        {inCart}
                      </span>
                    )}
                  </button>
                )
              })}
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        </div>

        {/* §9.311, owner P1 - *"košík is not scrollable, I need to scroll down the whole webpage"*.
          *
          * **The sticky was here and the bound was not.** A sticky element taller than the viewport
          * pins until its own bottom edge arrives, so reaching the basket's controls meant scrolling
          * the document - measured at 1920×1080, this panel was already 123 px below the fold with a
          * modest basket.
          *
          * **§9.313 replaced the `100vh` cap with `h-full`.** The page is a bounded row now, so the
          * column already knows its height and a second viewport calculation here would be a way for
          * the two to disagree.
          *
          * **The whole panel, not only the item list.** This column is the basket *and* the customer,
          * table, waiter, discount and warning fields; a `flex-1` item list inside a fixed-height
          * panel would collapse to nothing where those fields are long, and hidden content is worse
          * than a scrollbar. */}
        <div
          data-cart-panel
          className="bg-card border border-border rounded-2xl p-4 space-y-4 lg:h-full lg:min-h-0 lg:overflow-y-auto"
        >
          {/* §9.312, owner P3 - *"make it when I scroll, I see «Košík», with like: Košík (xx products)
            * and on right side «vyprázdniť celý košík»"*.
            *
            * **Pinned to the top of the scrolling panel**, which is where P2 moved the stickiness
            * from the submit button. Legacy has this header already - `order-cart.tsx:88`, icon,
            * title, count, and a clear action pushed right with `ml-auto` - and AGOS had ported the
            * title alone. There was **no way to empty the basket at all**: every line had to be
            * removed one at a time.
            *
            * `-mx-4 -mt-4 px-4 pt-4` and the ground are what stop the rows scrolling *through* it. */}
          <div className="sticky top-0 z-10 -mx-4 -mt-4 flex items-center gap-2 border-b border-border bg-card px-4 pb-2 pt-4">
            <ShoppingCart className="w-4 h-4 shrink-0 text-primary" />
            <p className="text-sm font-bold" data-cart-heading>
              <T>Košík</T>
              {cart.length > 0 && (
                <span className="ml-1.5 font-medium text-muted-foreground" data-cart-count>
                  ({basketCountLabel(basketCount(cart))})
                </span>
              )}
            </p>
            {cart.length > 0 && (
              <button
                type="button"
                data-clear-cart
                onClick={() => setConfirmClear(true)}
                className="ml-auto text-xs font-medium text-destructive transition-colors hover:text-destructive/80"
              >
                <T>Vyprázdniť celý košík</T>
              </button>
            )}
          </div>

          <CartItemsList
            cart={cart}
            onQuantityChange={(lineIndex, delta) => {
              // §9.712, #136 - the basket's *+* is an added unit, asked about like the tile's (`asksUpsellFor`).
              // §9.713 - and the line is given a `lineId` first, so what is chosen can be put under it.
              const line = cart[lineIndex]
              const asks = asksAboutAddons && delta > 0 && line !== undefined && asksUpsellFor(line)
              const parentLineId = asks ? withLineId(cart, lineIndex).lineId : null
              updateCart((current) =>
                changeQuantity(
                  parentLineId === null
                    ? current
                    : current.map((entry, index) =>
                        index === lineIndex && entry.lineId === undefined ? { ...entry, lineId: parentLineId } : entry,
                      ),
                  lineIndex,
                  delta,
                ),
              )
              if (line !== undefined && parentLineId !== null) {
                offerUpsellFor({ id: line.productId, name: line.productName }, parentLineId)
              }
            }}
            onRemoveLine={(lineIndex) => updateCart((current) => removeLine(current, lineIndex))}
            onRemoveBoxGroup={(boxGroupId) =>
              updateCart((current) => removeBoxGroup(current, boxGroupId))
            }
            onEditExtras={(lineIndex) => setExtrasLine(lineIndex)}
            onReopenPizza={reopenPizza}
            // §9.690, owner #3 - a note per product, for the kitchen.
            onLineNoteChange={(lineIndex, note) =>
              updateCart((current) => setLineNote(current, lineIndex, note))
            }
          />

          {giftOffers.map((offer) => (
            <button
              key={`${offer.ruleType}:${offer.ruleId}`}
              type="button"
              data-gift-offer={offer.ruleId}
              data-gift-offer-kind={offer.ruleType}
              onClick={() => updateCart((current) => [...current, ...giftLinesFor(offer, current)])}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-600 dark:text-amber-400 text-xs font-bold hover:bg-amber-500/20 transition-colors"
            >
              <Gift className="w-3.5 h-3.5" /> {offer.count}× {offer.giftName} <T>zdarma</T>
            </button>
          ))}

          {needs.table && (
            <LabelledField label={tt('Stôl')}>
              {/* §9.267 - the placeholder was a bare `"S4"`: the one string on this screen outside
                * `tt()`, and one that reads like a value somebody left behind rather than a hint. */}
              <input
                value={tableName}
                onChange={(event) => setTableName(event.target.value)}
                placeholder={tt('napr. S4')}
                aria-label={tt('Stôl')}
                className={FIELD_CLASSES}
              />
            </LabelledField>
          )}

          {/* §9.40 - who is serving this table. `employeeId` has been in the payload since it was
            * written and nothing set it, so every table order was saved with no waiter against it.
            * The list is who is *on shift*; §9.32's clock-in is what puts somebody on it. */}
          {needs.table && (
            <LabelledField label={tt('Čašník')}>
              <select
                value={waiterId === null ? '' : String(waiterId)}
                onChange={(event) =>
                  setWaiterId(event.target.value === '' ? null : Number(event.target.value))
                }
                aria-label={tt('Čašník')}
                data-order-waiter
                className={FIELD_CLASSES}
              >
                <option value="">
                  {waiterPlaceholder(waiterList)}
                </option>
                {waiters.map((waiter) => (
                  <option key={waiter.id} value={waiter.id}>
                    {waiter.name}
                  </option>
                ))}
              </select>
              {/* §9.633, FB-948 - a way out, because this read does not poll: without it a failed shift list
                * blocks the save until somebody reloads the page, and a reload on the order builder throws
                * away the basket. Beside the field rather than over the screen - the rest of the form is
                * fine. */}
              {waitersFailed && (
                <button
                  type="button"
                  data-waiters-retry
                  onClick={() => void refetchWaiters()}
                  className="mt-1 text-[11px] font-semibold text-primary"
                >
                  <T>Skúsiť znova</T>
                </button>
              )}
            </LabelledField>
          )}

          {/* Name, telephone, e-mail and address moved into the `CustomerPanel` at the top of the main
            * column — §9.14. Two copies of one field is how they disagree. */}
          {/* §9.786, owner O139 - the courier's reward is the fee: the quote's figure, which the person on the phone may
            * change. The zones below are not consulted in this mode, on either side. */}
          {couriersDeliver && editing === null && (
            <>
              <p
                className={cn(
                  'text-[11px] px-1',
                  courierFeeCents === null || courierQuote?.withinRange === false
                    ? 'text-amber-500'
                    : 'text-muted-foreground',
                )}
                data-courier-fee-note
              >
                {tillCourierNote(courierQuote, { hasPoint: deliveryPoint !== null, failed: pointQuote.isError })}
              </p>
              <LabelledField label={tt('Doprava (odmena šoféra)')}>
                <input
                  value={courierFee}
                  onChange={(event) => setCourierFee(event.target.value)}
                  inputMode="decimal"
                  placeholder={courierQuote?.feeCents == null ? '' : formatDecimal(courierQuote.feeCents / 100, 2)}
                  aria-label={tt('Doprava (odmena šoféra)')}
                  data-courier-fee
                  className={FIELD_CLASSES}
                />
              </LabelledField>
            </>
          )}

          {needs.address && !(couriersDeliver && editing === null) && (
            <>
              {/* The zone, not a geocoded point: the zones are named after the villages the person on
                * the phone is already asking about. This also cited `GET /geocode/search` answering
                * "Google Maps API key not configured" — untrue since 2026-08-03, corrected 2026-08-05.
                * See `delivery-fee.ts` for which half of the reasoning survived the key. */}
              {/* §9.161 - legacy has no picker here, it has this line. The zone follows the address
                * (§9.119) and the fee follows the zone; asking a dispatcher to confirm what the
                * address already decided is the control the owner asked to remove. */}
              <p
                className={cn(
                  'text-[11px] px-1',
                  zone === null || belowMinimum(zone, goodsCents)
                    ? 'text-amber-500'
                    : 'text-muted-foreground',
                )}
                data-delivery-fee-note
              >
                {/* §9.354 - the count, so a branch with no zones says so instead of blaming the
                    address. Sereď and Nesvady have none; Šurany has 31. */}
                {feeNote(zone, goodsCents, customerAddress, zoneChoices.length)}
              </p>

              {/* The one state that still needs a person: an address outside every zone. Legacy says
                * *"zadajte cenu dovozu ručne nižšie"* and means this field. Shown only here, so an
                * ordinary order keeps its single automatic answer. */}
              {zone === null && customerAddress.trim() !== '' && (
                <LabelledField label={tt('Cena dovozu ručne')}>
                  <input
                    value={manualDeliveryFee}
                    onChange={(event) => setManualDeliveryFee(event.target.value)}
                    inputMode="decimal"
                    aria-label={tt('Cena dovozu ručne')}
                    data-manual-delivery-fee
                    className={FIELD_CLASSES}
                  />
                </LabelledField>
              )}
            </>
          )}

          <CartDiscountSection
            /* §9.744 - each box on its own right. Hidden, never emptied: the state below is the order's own
               figure whether or not its box is drawn. */
            boxes={discountBoxes}
            discountMode={discountMode}
            /* §9.744 amended - typing, switching the unit and clearing are what touch the box; a press of the unit
               already chosen changes nothing and touches nothing. */
            onDiscountModeChange={(mode) => {
              if (mode !== discountMode) setDiscountTouched(true)
              setDiscountMode(mode)
            }}
            discountValue={discountValue}
            onDiscountValueChange={(value) => {
              setDiscountTouched(true)
              setDiscountValue(value)
            }}
            couponCode={couponCode}
            onCouponCodeChange={setCouponCode}
            coupon={coupon}
            onClearCoupon={() => {
              setCoupon(null)
              setCouponCode('')
            }}
            onApplyCoupon={() => void applyCoupon()}
          />

          <CartPaymentMethods
            value={paymentMethod}
            onChange={setPaymentMethod}
            // A table is settled at the table; a courier's card reader is not there.
            exclude={needs.table ? ['card_courier'] : undefined}
          />

          <CartNotes value={notes} onChange={setNotes} />

          {/* Legacy renders it under the notes, and it is offered while editing too - an order being
            * changed reopens on its own time (`partsOf`) rather than losing it. */}
          <CartPreorder
            enabled={preorderOn}
            onEnabledChange={(next) => {
              // Legacy clears both on the way off, so a switched-off časenka leaves nothing behind for
              // the next order to send.
              scheduleFor(next, next ? preorderDate : '')
              if (!next) setPreorderTime('')
            }}
            date={preorderDate}
            time={preorderTime}
            onDateChange={(next) => scheduleFor(preorderOn, next)}
            onTimeChange={setPreorderTime}
            minDate={todayInZone(new Date())}
            minTime={earliestTime(preorderDate, new Date())}
            noTimeLeft={noTimeLeftToday(preorderDate, new Date())}
            scheduledLabel={scheduledLabel}
          />

          {menuMismatch && (
            <div
              data-lunch-mismatch
              className="flex flex-col gap-1 bg-red-500/10 border border-red-500/25 rounded-xl px-3 py-2 text-xs text-red-500 font-semibold"
            >
              <div className="flex items-center gap-2">
                <X className="w-3.5 h-3.5 shrink-0" /> {tt('Obedové menu nesúhlasí s Časenkou')}
              </div>
              <div className="flex items-center gap-2 pl-5 text-[11px] font-medium opacity-90">
                <span>
                  <T>Zobrazené:</T> {formatDayLabel(lunchDate, new Date())} <T>· Doručenie:</T>{' '}
                  {formatDayLabel(preorderDate, new Date())}
                </span>
                <button
                  type="button"
                  data-lunch-mismatch-fix
                  onClick={() => setLunchDate(preorderDate)}
                  className="px-2 py-0.5 rounded-md bg-red-500/20 hover:bg-red-500/30 text-[11px] font-bold transition-all"
                >
                  <T>Nastaviť na Časenku</T>
                </button>
              </div>
            </div>
          )}

          <CartTotalsPanel
            totals={totals}
            showDelivery={needs.address}
            discountLabel={
              totals.manualDiscountCents > 0
                ? `${tt('Zľava')} (${discountValue}${discountMode === 'percent' ? '%' : '€'})`
                : undefined
            }
            couponCode={coupon?.code ?? null}
          />

          {/* §9.111 - said before the order is taken, not discovered as a 400 at payment. A warning,
            * not a block: the order can still be created, and often should be. */}
          {unprintableNote !== null && (
            <p
              className="flex items-start gap-1.5 text-[11px] text-amber-500"
              data-unprintable-warning
            >
              <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
              {unprintableNote}
            </p>
          )}

          {/* §9.312, owner P2 - *"that «Vytvoriť objednávku» should not be sticked"*. §9.311 pinned it
            * to the panel's bottom so it stayed reachable; the owner would rather the **header**
            * stayed put, which it now does. The panel's viewport cap from §9.311 remains, so the
            * button is still a short scroll away rather than a page away. */}
          <button
            type="button"
            onClick={submit}
            data-submit-order
            /* §9.433 - **`updateOrder` as well.** This read `createOrder.isPending` alone, which is
             * never true on an edit, so the button stayed live while a PATCH was in flight and a
             * second click sent a second one. */
            disabled={saving || cart.length === 0 || menuMismatch}
            className={cn('w-full flex items-center justify-center gap-2 py-3 rounded-xl', BUTTON_PRIMARY, 'text-sm disabled:cursor-not-allowed')}
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4" />
            )}
            {/* §9.433, owner request - it said *Vytvoriť objednávku* while editing one. Two
              * elements rather than a ternary inside `<T>`, so both strings stay greppable in the
              * shape every other label here has. */}
            {editId === null ? <T>Vytvoriť objednávku</T> : <T>Upraviť objednávku</T>}
          </button>
        </div>
      </div>

      {/* §9.312, owner P3 - the basket is emptied only when somebody says so twice. */}
      <ConfirmDialog
        open={confirmClear}
        onClose={() => setConfirmClear(false)}
        onConfirm={() => {
          updateCart(() => [])
          setConfirmClear(false)
        }}
        title={tt('Vyprázdniť celý košík?')}
        description={tt('Všetky položky sa z košíka odstránia. Túto akciu nie je možné vrátiť.')}
        confirmLabel={tt('Vyprázdniť')}
        variant="danger"
      />

      {/* §9.620, owner R2 - opened by the server's own `FISKAL_DOCUMENT_LIVE`, never by a rule of this
        * screen's, and both of its routes save the order. The patch it re-sends is the one that was
        * refused, held in `fiskalQuestion`. */}
      {fiskalQuestion !== null && editing !== null && (
        <FiskalDecisionDialog
          order={editing}
          saving={updateOrder.isPending}
          onStornoed={() => sendEdit(editing.id, fiskalQuestion)}
          onKeep={(reason) => sendEdit(editing.id, fiskalQuestion, reason)}
          onClose={() => setFiskalQuestion(null)}
        />
      )}

      {/* §9.41 - toppings on a pizza off the menu. `setLineExtras` takes the previous surcharge off
        * `unitPrice` before adding the new one, so editing twice does not accumulate. */}
      {/* §9.90 - a dialog, as the kombo is: the owner, on finding the two different, *"vlastná pizza
        * is still not popup like kombo pizza, but different thing"*. The panel's own markup is
        * unchanged; only its chrome and where it sits have moved, so the builder behaves exactly as
        * it did while looking like its neighbour. */}
      {pizzaOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-pizza-dialog>
          <button
            type="button"
            className="absolute inset-0 bg-black/60"
            aria-label={tt('Zavrieť')}
            onClick={closePanel}
          />
          {/* §9.488 - the third overlay on the order-taking path, after §9.485's two. Named with
              `aria-label` rather than `labelledby`: its title is a `<p>`, and putting an id on that to point
              at would claim it is a heading - a second defect bolted onto the fix for the first. */}
          <section
            role="dialog"
            aria-modal="true"
            aria-label={tt('Vlastná pizza')}
            ref={pizzaPanelRef}
            onKeyDown={pizzaKeyDown}
            className="relative w-full max-w-lg max-h-[85vh] flex flex-col rounded-2xl bg-background border border-border overflow-hidden"
          >
            <header className="px-5 pt-4 pb-3 border-b border-border shrink-0 flex items-center justify-between gap-3">
              <p className="flex items-center gap-2 text-sm font-bold">
                <Pizza className="w-4 h-4 text-primary" />
                <T>Vlastná pizza</T>
              </p>
              <button
                type="button"
                data-dialog-cancel
                aria-label={tt('Zavrieť')}
                data-pizza-dialog-close
                onClick={closePanel}
                className="p-1.5 rounded-lg hover:bg-muted shrink-0"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </header>
            <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-3" data-pizza-panel>
              {/* §9.680, owner #17 - legacy's optional *Názov pizze* (`pizza-builder-modal.tsx:185-195`), which the
                * port dropped. Kept on the draft, so `setPizza(newPizza(...))` after an add clears it with the
                * rest, and a reopened pizza brings it back (`draftFromParts`' fourth argument). */}
              <label className="block space-y-1.5">
                <span className="block text-sm font-bold">
                  <T>Názov pizze</T>
                </span>
                <input
                  value={pizza.name ?? ''}
                  onChange={(event) => {
                    const typed = event.target.value
                    setPizza((current) => ({ ...current, name: typed }))
                  }}
                  maxLength={PIZZA_NAME_MAX}
                  placeholder={tt('Vlastná pizza (voliteľné)')}
                  data-pizza-name
                  className={FIELD_CLASSES}
                />
              </label>

              <section className="space-y-1.5">
                <h3 className="text-sm font-bold">
                  <T>Veľkosť</T>
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {sizesOf(pizzaConfig).map((size) => (
                    <CategoryChip
                      key={size.cm}
                      active={pizza.sizeCm === size.cm}
                      onClick={() => setPizza((current) => ({ ...current, sizeCm: size.cm }))}
                      label={`${size.label} · ${formatCents(parseMoneyToCents(size.basePrice))}`}
                      testId={`pizza-size-${size.cm}`}
                    />
                  ))}
                </div>
              </section>

              <section className="space-y-1.5">
                <h3 className="text-sm font-bold">
                  <T>Základ</T>
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {/* §9.681, owner #18a - the franchise's own bases, the *Pizza ingrediencie* rows the web
                    * already offered (§9.647). This listed `config.bases`, which on Šereď's nameless config
                    * is empty, so no base could be chosen and *Vyberte základ* kept the add button off. §9.679 -
                    * priced from the row, per size; the till keeps the figure on its chip, the web does not. */}
                  {basesFor(pizzaConfig, ingredients).map((base) => (
                    <CategoryChip
                      key={base.value}
                      active={pizza.baseValue === base.value}
                      onClick={() => setPizza((current) => ({ ...current, baseValue: base.value }))}
                      label={`${base.label} · ${formatCents(basePriceCents(base.value, pizzaConfig, pizza.sizeCm, ingredients))}`}
                      testId={`pizza-base-${base.value}`}
                    />
                  ))}
                </div>
              </section>

              <section className="space-y-3">
                <h3 className="text-sm font-bold">
                  <T>Ingrediencie</T>
                </h3>
                {/* §9.25 - under the category each was filed in, legacy's order and legacy's
                  * `sortOrder` inside it. This was one flat 38-tile grid in whatever order the
                  * endpoint answered, and it included the two *Základ* rows — the pizza's own base,
                  * offered a second time as something to charge for on top of it. */}
                {ingredientGroups(ingredients).map((group) => (
                  <div key={group.category} className="space-y-1.5">
                    <p
                      className="text-xs font-bold uppercase tracking-wider text-muted-foreground"
                      data-pizza-ingredient-group={group.category}
                    >
                      <T>{group.category}</T>
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                      {group.ingredients.map((ingredient) => (
                        <button
                          key={ingredient.id}
                          type="button"
                          data-pizza-ingredient={ingredient.id}
                          data-pizza-qty={ingredientQty(pizza, ingredient.id)}
                          aria-pressed={ingredientQty(pizza, ingredient.id) > 0}
                          onClick={() =>
                            setPizza((current) => toggleIngredient(current, ingredient.id))
                          }
                          // §9.678, owner #18b - one tap adds, the second and third add a portion,
                          // a fourth removes. **One colour for every chosen count**: *"nech je tam
                          // 2x 3x a nie nech mení farby"* supersedes §9.43's orange double, and the
                          // large count below is what tells them apart.
                          className={cn(
                            'text-left rounded-xl border p-2 text-xs transition-colors',
                            ingredientQty(pizza, ingredient.id) === 0
                              ? 'border-border bg-card hover:border-primary/50'
                              : 'border-primary bg-primary/10 font-semibold',
                          )}
                        >
                          <span className="flex items-baseline gap-1">
                            <span className="truncate">{ingredient.name}</span>
                            {ingredientQty(pizza, ingredient.id) > 0 && (
                              <span
                                className="ml-auto shrink-0 text-sm font-extrabold tabular-nums text-primary"
                                data-pizza-portions
                              >
                                {portionLabel(ingredientQty(pizza, ingredient.id))}
                              </span>
                            )}
                          </span>
                          {pizza.sizeCm !== null && (
                            <span className="block text-[10px] text-muted-foreground tabular-nums">
                              {/* §9.678 - the price of every portion on it, as the total counts them. */}
                              {formatCents(
                                portionPriceCents(ingredient, pizza.sizeCm, ingredientQty(pizza, ingredient.id)),
                              )}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </section>

              {/* §9.93 - no crust control here either. It stayed behind when the customer's went,
                * and by then the crust had left the price: taking one changed nothing at all, which
                * is a control that lies. An `Okraj` row is offered among the toppings above. */}

              <p className="text-xs text-muted-foreground" data-pizza-summary>
                {pizzaSummary(pizza, pizzaConfig, ingredients)}
              </p>
              {pizzaProblems(pizza, pizzaConfig).map((problem) => (
                <p key={problem} className="text-[11px] text-amber-500" data-pizza-problem>
                  {problem}
                </p>
              ))}
              <button
                type="button"
                data-pizza-add
                disabled={pizzaProblems(pizza, pizzaConfig).length > 0}
                onClick={() => {
                  const name = pizzaLineName(pizza, pizzaConfig, ingredients)
                  updateCart((current) =>
                    addProduct(current, {
                      id: pizzaCartId(name),
                      name,
                      price: pizzaPriceCents(pizza, pizzaConfig, ingredients) / 100,
                      categoryName: tt('Vlastná pizza'),
                      // §10A.14 — size, base, toppings, crust. The same helper the customer app
                      // uses, and its sum is `pizzaPriceCents` above by construction.
                      parts: pizzaParts(pizza, pizzaConfig, ingredients),
                      // §9.703, FB-1009 - the draft itself, so the line opens in this builder again.
                      pizzaDraft: pizza,
                      // §9.724 - a reopened pizza's note, back on the line it is rebuilt as.
                      ...(reopenedNote === undefined ? {} : { note: reopenedNote }),
                    }),
                  )
                  setReopenedNote(undefined)
                  setPizza(newPizza(pizzaConfig, ingredients))
                }}
                className={cn('w-full min-h-[48px] rounded-xl', BUTTON_PRIMARY)}
              >
                <T>Pridať pizzu do košíka</T>
              </button>
            </div>
            </div>
          </section>
        </div>
      )}
      {komboOpen && (
        <KomboPizzaDialog
          // §9.798 - keyed on the count, so a setting read after the dialog opened redraws its selects.
          key={`${komboCount.min}-${komboCount.max}`}
          pizzas={komboPizzas}
          komboCm={komboCm}
          count={komboCount}
          onClose={() => setKomboOpen(false)}
          onAdd={({ name: halves, unitPriceCents, parts, composition }) => {
            // §9.430 - the prefix, which this had never stored. `komboName` returns a bare
            // `A + B + C`, and the kitchen card recovers a kombo's three pizzas by stripping the
            // prefix off the name: without one the card showed three names and no ingredients, the
            // same end as the customer app's wrong case. One builder for both, in `@agos/shared`.
            const name = komboLineName(halves)
            // A composed line, like a custom pizza: a negative id keeps `ServerPricing` from
            // re-pricing it from a catalogue row it has none of (§10A.9) — and the parts let it
            // bound the line by what it is made of instead (§10A.14).
            //
            // §10A.26 — `composition` says *which* bound. Dropping it here is not visible on the
            // screen, in the cart, or in either payload-builder's own tests: the total is right the
            // whole way and the server simply applies the looser rule. An e2e reading the wire is
            // what caught it.
            updateCart((current) =>
              addProduct(current, {
                id: pizzaCartId(name),
                name,
                price: unitPriceCents / 100,
                categoryName: tt('Kombo pizza'),
                ...(parts ? { parts } : {}),
                ...(composition ? { composition } : {}),
              }),
            )
            setKomboOpen(false)
          }}
        />
      )}

      {addonPrompt !== null && (
        <AddonPrompt
          // A different line's question is a fresh prompt; a second unit of the same line keeps what was ticked.
          key={`${String(addonPrompt.productId)}|${addonPrompt.parentKey}`}
          productName={addonPrompt.productName}
          offer={addonPrompt.offer}
          units={addonPrompt.units}
          // §9.662, FB-990 - the owner asked for the till's to be wider: three in a row from `sm:` up. The
          // customer's bottom sheet keeps one per row, which is why this is a prop rather than a change.
          wide
          onClose={() => setAddonPrompt(null)}
          onConfirm={(chosen) => {
            /* An add-on is an ordinary product - its own id, price and VAT - exactly as the customer app adds it, and
               the receipt prints a dressing. §9.713: it goes in as a line **of the line the prompt was opened for**
               (`parentKey` is that line's `lineId`), so Nová objednávka draws it under that product and the kitchen
               nests it there. §9.712: each with the price it was quoted at (0 is *zadarmo*) and its group, which the
               server re-prices from and holds to the per-unit limit (§10A.366). */
            const parentLineId = addonPrompt.parentKey
            updateCart((current) => addAddonLines(current, parentLineId, chosenAddonsOf(chosen)))
            setAddonPrompt(null)
          }}
        />
      )}

      {extrasLine !== null && cart[extrasLine] !== undefined && (
        <PizzaExtrasDialog
          productName={cart[extrasLine]?.productName ?? ''}
          // §9.730 - the price before the toppings it already carries, which `setLineExtras` takes off on the save.
          // Handed the whole `unitPrice`, a reopened pizza with ham quoted the ham twice.
          unitPrice={extrasBasePrice(cart[extrasLine])}
          ingredients={ingredients}
          initialExtras={cart[extrasLine]?.pizzaExtras}
          onClose={() => setExtrasLine(null)}
          onConfirm={(extras, extrasPriceCents, chosen) => {
            updateCart((current) =>
              setLineExtras(current, extrasLine, extras, extrasPriceCents, chosen),
            )
            setExtrasLine(null)
          }}
        />
      )}
    </div>
  )
}

/**
 * A chip in the builder's category row.
 *
 * `colour` is §9.15 and it applies **only when the chip is not selected**. Selected keeps the primary
 * fill it has always had: the colour is there to help find a category in a row of twenty, and the
 * highlight is there to say which one you are in. Letting the colour win would take away the second
 * answer to get a louder version of the first.
 */
function CategoryChip({
  active,
  onClick,
  label,
  testId,
  colour = null,
}: {
  active: boolean
  onClick: () => void
  label: string
  /** A hyphenated `data-*` passed straight to a component is dropped, so it is named here. */
  testId?: string
  colour?: CategoryColour | null
}) {
  const tinted = !active && colour !== null
  return (
    <button
      type="button"
      data-chip={testId}
      // §9.42 — which chip is lit, as an attribute. Two were, and nothing in the build could say so;
      // the channel row states the same thing the same way (`data-order-channel-active`).
      data-chip-active={active ? 'yes' : 'no'}
      data-chip-colour={colour ?? undefined}
      onClick={onClick}
      style={tinted ? categoryChipStyle(colour) : undefined}
      className={cn(
        'px-3 py-1.5 rounded-lg text-xs font-bold transition-colors border',
        active
          ? 'bg-primary text-primary-foreground border-primary'
          : tinted
            ? ''
            : 'bg-muted hover:bg-muted/70 text-muted-foreground border-transparent',
      )}
    >
      {label}
    </button>
  )
}

function LabelledField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
        {label}
      </span>
      {children}
    </label>
  )
}

export type { OrderPayload }
